import React, { useState, useEffect, useRef, useCallback } from "react";
import { Check, ListTodo, Plus, Search, Target, Trash2, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import {
  MONTHS_DE, DAYS_SHORT, APPOINTMENT_COLORS, COLOR_KEYS, STORAGE_KEYS,
  DEFAULT_MULTIDAY_COLOR, DEFAULT_SINGLEDAY_COLOR,
  getAppointmentColor
} from "@/utils/calendarConstants";
import {
  getDaysInMonth, getFirstDayOfMonth, getLocalDateStr, getWeekNumber
} from "@/utils/calendarUtils";
import AppointmentModal from "@/components/calendar/AppointmentModal";
import { userStorage } from '../../utils/userStorage';
const API = process.env.REACT_APP_BACKEND_URL;

export default function MonthView({ year, month, onDayClick, monthGoals, onMonthGoalChange, tasks, onTaskChange, appointments, yearPlanning, monthAppointments, onMonthAppointmentsChange, onYearPlanningChange, onDeleteFromAll, onGoToWeek, onTransferToWeek, viewMode = 'calendar', todoCalendarData, onTodoCalendarHandled, onEditTodo, todoEditRefreshKey = 0, onToggleDoneByTaskId }) {
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalText, setGoalText] = useState('');
  const [newTask, setNewTask] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [newAppointment, setNewAppointment] = useState({ title: '', from: '', to: '', note: '', priority: 5, color: '', contactRef: null });
  const [editingAppointment, setEditingAppointment] = useState(null);
  
  // Kontakt-Verknüpfung für das Add/Edit-Modal
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [contactSearchOpen, setContactSearchOpen] = useState(false);
  const contactSearchRef = useRef(null);
  
  // Month Todos Modal state
  const [showMonthTodosModal, setShowMonthTodosModal] = useState(false);
  const [newMonthTodoText, setNewMonthTodoText] = useState('');
  const [selectedMonthWeek, setSelectedMonthWeek] = useState(null);
  const [monthTodosRefresh, setMonthTodosRefresh] = useState(0);
  const [focusedKW, setFocusedKW] = useState(null); // KW to scroll to when modal opens
  const [pendingTransferTodo, setPendingTransferTodo] = useState(null); // For transfer-complete event
  
  // Function to open Month Todos Modal for a specific KW
  const openMonthTodosForKW = (weekNumber) => {
    setFocusedKW(weekNumber);
    setShowMonthTodosModal(true);
  };

  // Kontakt-Suche: Live-Search useEffect
  useEffect(() => {
    if (!showAddModal || !contactSearch.trim() || newAppointment.contactRef) {
      if (!newAppointment.contactRef) setContactResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/api/teampartner?typ=`);
        const q = contactSearch.toLowerCase();
        setContactResults((res.data || []).filter(c => c.name?.toLowerCase().includes(q)).slice(0, 6));
      } catch { setContactResults([]); }
    }, 200);
    return () => clearTimeout(timer);
  }, [contactSearch, showAddModal, newAppointment.contactRef]);

  // Click-outside für Kontakt-Suche
  useEffect(() => {
    const handleClick = (e) => {
      if (contactSearchRef.current && !contactSearchRef.current.contains(e.target)) setContactSearchOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  
  // Transfer modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferWeekData, setTransferWeekData] = useState(null);
  const [transferItems, setTransferItems] = useState([]);
  const [currentTransferIndex, setCurrentTransferIndex] = useState(0);
  const [transferEditData, setTransferEditData] = useState({});
  const [transferredInSession, setTransferredInSession] = useState([]);
  const [showTransferColorPicker, setShowTransferColorPicker] = useState(false);
  const [transferOverlapError, setTransferOverlapError] = useState('');
  const [transferAddedAppointments, setTransferAddedAppointments] = useState([]); // Track appointments added in this transfer session
  
  // Get current color for display in modal
  const isMultiDayAppointment = newAppointment.from && newAppointment.to && newAppointment.from !== newAppointment.to;
  const currentColorKey = newAppointment.color || (isMultiDayAppointment ? DEFAULT_MULTIDAY_COLOR : DEFAULT_SINGLEDAY_COLOR);
  const currentColorData = APPOINTMENT_COLORS[currentColorKey];
  
  const monthKey = `${year}-${month}`;
  const yearMonthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

  // Wizard-Daten für Monatsanzeige
  const WIZARD_KEY = "burningteam_wizard_goals";
  const [wizardData, setWizardData] = useState(() => {
    try { return JSON.parse(userStorage.getItem(WIZARD_KEY)); } catch { return null; }
  });
  useEffect(() => {
    const refresh = () => {
      try { setWizardData(JSON.parse(userStorage.getItem(WIZARD_KEY))); } catch { setWizardData(null); }
    };
    window.addEventListener("burningteam-wizard-sync", refresh);
    return () => window.removeEventListener("burningteam-wizard-sync", refresh);
  }, []);
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date();
  const todayStr = getLocalDateStr(today);
  const getWeekNum = (date) => {
  
  // Helper: Get week number
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };
  
  // Helper: Get all weeks in current month with date ranges
  const getWeeksInMonth = () => {
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    
    const weeks = [];
    let currentDate = new Date(firstDayOfMonth);
    
    while (currentDate <= lastDayOfMonth) {
      const weekNum = getWeekNum(currentDate);
      
      // Find the start of this week (Monday)
      const weekStart = new Date(currentDate);
      const dayOfWeek = weekStart.getDay() || 7;
      weekStart.setDate(weekStart.getDate() - dayOfWeek + 1);
      
      // Find the end of this week (Sunday)
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      // Only add if not already in list
      if (!weeks.find(w => w.weekNum === weekNum)) {
        weeks.push({
          weekNum,
          startDate: getLocalDateStr(weekStart),
          endDate: getLocalDateStr(weekEnd),
          startDay: weekStart.getDate(),
          startMonth: weekStart.getMonth(),
          endDay: weekEnd.getDate(),
          endMonth: weekEnd.getMonth(),
          isCurrent: weekNum === getWeekNum(today)
        });
      }
      
      // Move to next week
      currentDate.setDate(currentDate.getDate() + 7 - (currentDate.getDay() || 7) + 1);
    }
    
    return weeks;
  };
  
  // Load/Save month todos from localStorage
  const loadMonthTodos = () => {
    try {
      const stored = JSON.parse(userStorage.getItem(STORAGE_KEYS.monthlyTodos) || '{}');
      if (Array.isArray(stored)) {
        return stored; // Old format
      }
      return stored[yearMonthKey] || [];
    } catch (e) {
      return [];
    }
  };
  
  const saveMonthTodos = (todos) => {
    try {
      const stored = JSON.parse(userStorage.getItem(STORAGE_KEYS.monthlyTodos) || '{}');
      if (Array.isArray(stored)) {
        // Migrate old format
        const migrated = {};
        migrated[yearMonthKey] = todos;
        userStorage.setItem(STORAGE_KEYS.monthlyTodos, JSON.stringify(migrated));
      } else {
        stored[yearMonthKey] = todos;
        userStorage.setItem(STORAGE_KEYS.monthlyTodos, JSON.stringify(stored));
      }
    } catch (e) {}
  };
  
  // Get month todos grouped by week
  // Für die aktuelle KW werden zusätzlich die weeklyTodos ("Diese Woche") eingeblendet
  const getMonthTodosGrouped = () => {
    const todos = loadMonthTodos();
    const weeksInMonth = getWeeksInMonth();
    const now = new Date();
    const thisWeekNum = getWeekNum(now);
    const grouped = { unassigned: [] };
    
    weeksInMonth.forEach(week => {
      grouped[week.weekNum] = [];
    });
    
    todos.forEach(t => {
      if (t.weekNum === undefined || t.weekNum === null) {
        grouped.unassigned.push(t);
      } else if (grouped[t.weekNum]) {
        grouped[t.weekNum].push(t);
      } else {
        grouped.unassigned.push(t);
      }
    });

    // Aktuelle KW: weekly todos ("Diese Woche") ebenfalls anzeigen
    if (grouped[thisWeekNum] !== undefined) {
      try {
        const weekKey = `${now.getFullYear()}-W${thisWeekNum}`;
        const storedWeekly = JSON.parse(userStorage.getItem(STORAGE_KEYS.weeklyTodos) || '{}');
        const weeklyThisWeek = (storedWeekly[weekKey] || []).map(t => ({
          ...t,
          _fromWeekly: true // Marker damit wir wissen woher es kommt
        }));
        grouped[thisWeekNum] = [...grouped[thisWeekNum], ...weeklyThisWeek];
      } catch (e) {}
    }
    
    return grouped;
  };
  
  // Add month todo
  const addMonthTodo = (weekNumOrUnassigned) => {
    if (!newMonthTodoText.trim()) return;
    // Block adding todos for past weeks
    if (weekNumOrUnassigned !== 'unassigned') {
      const weeksInMonth = getWeeksInMonth();
      const weekInfo = weeksInMonth.find(w => w.weekNum === weekNumOrUnassigned);
      if (weekInfo) {
        const endDate = new Date(weekInfo.endDate);
        const todayCheck = new Date();
        todayCheck.setHours(0, 0, 0, 0);
        if (endDate < todayCheck) return;
      }
    }
    
    const newTodo = {
      id: Date.now(),
      text: newMonthTodoText.trim(),
      done: false,
      priority: 5,
      createdAt: new Date().toISOString()
    };

    const now = new Date();
    const thisWeekNum = getWeekNum(now);

    // Aktuelle KW → direkt in weeklyTodos speichern (erscheint in TodosView "Diese Woche")
    if (weekNumOrUnassigned !== 'unassigned' && weekNumOrUnassigned === thisWeekNum) {
      const weekKey = `${now.getFullYear()}-W${thisWeekNum}`;
      const stored = JSON.parse(userStorage.getItem(STORAGE_KEYS.weeklyTodos) || '{}');
      const weekTodos = stored[weekKey] || [];
      weekTodos.push(newTodo);
      stored[weekKey] = weekTodos;
      userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(stored));
    } else {
      // Zukünftige KW oder ohne Zuweisung → wie bisher in monthlyTodos
      const todos = loadMonthTodos();
      if (weekNumOrUnassigned === 'unassigned') {
        todos.push(newTodo);
      } else {
        todos.push({ ...newTodo, weekNum: weekNumOrUnassigned });
      }
      saveMonthTodos(todos);
    }
    
    setNewMonthTodoText('');
    setMonthTodosRefresh(r => r + 1);
  };

  // Helper: check if a KW is fully in the past
  const isKWInPast = (weekNum) => {
    const weeksInMonth = getWeeksInMonth();
    const weekInfo = weeksInMonth.find(w => w.weekNum === weekNum);
    if (!weekInfo) return false;
    const endDate = new Date(weekInfo.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return endDate < today;
  };

  
  // Toggle month todo (oder weekly todo falls _fromWeekly)
  const toggleMonthTodo = (todoId, fromWeekly = false) => {
    if (fromWeekly) {
      const now = new Date();
      const weekKey = `${now.getFullYear()}-W${getWeekNum(now)}`;
      const stored = JSON.parse(userStorage.getItem(STORAGE_KEYS.weeklyTodos) || '{}');
      stored[weekKey] = (stored[weekKey] || []).map(t => t.id === todoId ? { ...t, done: !t.done } : t);
      userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(stored));
    } else {
      const todos = loadMonthTodos();
      const updated = todos.map(t => t.id === todoId ? { ...t, done: !t.done } : t);
      saveMonthTodos(updated);
    }
    setMonthTodosRefresh(r => r + 1);
  };
  
  // Delete month todo (oder weekly todo falls _fromWeekly)
  const deleteMonthTodo = (todoId, fromWeekly = false) => {
    if (fromWeekly) {
      const now = new Date();
      const weekKey = `${now.getFullYear()}-W${getWeekNum(now)}`;
      const stored = JSON.parse(userStorage.getItem(STORAGE_KEYS.weeklyTodos) || '{}');
      stored[weekKey] = (stored[weekKey] || []).filter(t => t.id !== todoId);
      userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(stored));
    } else {
      const todos = loadMonthTodos();
      const filtered = todos.filter(t => t.id !== todoId);
      saveMonthTodos(filtered);
    }
    setMonthTodosRefresh(r => r + 1);
  };
  
  // Format date range for display
  const formatDateRange = (week) => {
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    if (week.startMonth === week.endMonth) {
      return `${week.startDay}.-${week.endDay}. ${months[week.startMonth]}`;
    }
    return `${week.startDay}. ${months[week.startMonth]} - ${week.endDay}. ${months[week.endMonth]}`;
  };
  
  // Current week number
  const currentWeekNum = getWeekNum(today);

  // Clear pending transfer when modal closes (cancel case)
  useEffect(() => {
    if (!showAddModal) setPendingTransferTodo(null);
  }, [showAddModal]);

  // Handle todo-to-calendar transfer
  useEffect(() => {
    if (todoCalendarData && todoCalendarData.type === 'month') {
      // Set selected date from todo dueDate or first day of current month
      const dateStr = todoCalendarData.dueDate || `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const dayNum = parseInt(dateStr.split('-')[2]) || 1;
      setSelectedDay(dayNum);
      setNewAppointment({
        title: todoCalendarData.title || '',
        from: dateStr,
        to: dateStr,
        note: todoCalendarData.note || '',
        priority: todoCalendarData.priority || 5,
        color: '',
        contactRef: todoCalendarData.contactRef || null,
      });
      // Sync contactSearch display state
      setContactSearch(todoCalendarData.contactRef?.name || '');
      // Store pending transfer info so we can delete todo AFTER successful save
      if (todoCalendarData.todoId) {
        setPendingTransferTodo({ id: todoCalendarData.todoId, fromTab: todoCalendarData.fromTab });
      }
      setShowAddModal(true);
      // Clear the data
      if (onTodoCalendarHandled) {
        onTodoCalendarHandled();
      }
    }
  }, [todoCalendarData, year, month, onTodoCalendarHandled]);
  
  // Load month appointments from localStorage
  const getMonthAppointments = () => {
    return monthAppointments?.[yearMonthKey] || [];
  };

  // "Erledigt"-Toggle für Monats-Termine mit Kontakt-Verknüpfung
  const handleToggleMonthDone = async (apt) => {
    const newDone = !apt.done;
    setShowAddModal(false);
    setEditingAppointment(null);
    setContactSearch('');

    if (onToggleDoneByTaskId && apt.contactRef?.taskId) {
      // Zentraler Callback: aktualisiert Woche + Monat + Jahr + Kontakt-API
      await onToggleDoneByTaskId(apt.contactRef.taskId, newDone, apt.contactRef);
    } else {
      // Fallback: nur Monat aktualisieren
      const currentApts = getMonthAppointments();
      const updatedApts = currentApts.map(a => a.id === apt.id ? { ...a, done: newDone } : a);
      onMonthAppointmentsChange(yearMonthKey, updatedApts);
      if (apt.contactRef?.id && apt.contactRef?.taskId) {
        try {
          const endpoint = apt.contactRef.typ === 'ich'
            ? `${API}/api/auth/me/tasks/${apt.contactRef.taskId}`
            : `${API}/api/teampartner/${apt.contactRef.id}/tasks/${apt.contactRef.taskId}`;
          await axios.patch(endpoint, { done: newDone });
        } catch { }
      }
    }
  };
  
  useEffect(() => {
    setGoalText(monthGoals[monthKey] || '');
  }, [monthKey, monthGoals]);

  // Get all multi-day transferred items for the month (for span visualization)
  const getMultiDayItems = () => {
    // Get yearPlanning directly from localStorage for most up-to-date data
    let yearItems = yearPlanning?.[yearMonthKey] || [];
    try {
      const storedYearPlanning = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
      yearItems = storedYearPlanning?.[yearMonthKey] || yearItems;
    } catch (e) {}
    
    // Get monthAppointments from localStorage for most up-to-date data
    let monthApts = getMonthAppointments().filter(apt => apt.from !== apt.to);
    try {
      const storedMonth = JSON.parse(userStorage.getItem('zielplanung_month_appointments') || '{}');
      const storedApts = storedMonth?.[yearMonthKey] || [];
      if (storedApts.length > 0) {
        monthApts = storedApts.filter(apt => apt.from !== apt.to);
      }
    } catch (e) {}
    
    // Check transferredIds for marking items as transferred
    let transferredIds = [];
    try {
      transferredIds = JSON.parse(userStorage.getItem('zielplanung_transferred_ids') || '[]');
    } catch (e) {}
    
    // Track added IDs to avoid duplicates
    const addedIds = new Set();
    const result = [];
    
    // Include multi-day items from yearPlanning
    yearItems.forEach(item => {
      const isMultiDay = item.from !== item.to;
      if (!isMultiDay) return;
      // Skip year-only items from ZuErledigienModal
      if (item.yearOnly) return;
      
      // Skip if already transferred to month AND exists in monthAppts
      if (item.transferred) {
        const existsInMonth = monthApts.some(apt => 
          apt.id === item.id || 
          apt.originalYearId === item.id ||
          (apt.title === item.title && apt.from === item.from)
        );
        if (existsInMonth) return;
      }
      
      if (!addedIds.has(item.id)) {
        addedIds.add(item.id);
        result.push({ 
          ...item, 
          isYearItem: true,
          transferredToWeek: item.transferredToWeek || transferredIds.includes(item.id)
        });
      }
    });
    
    // Add month appointments (multi-day only)
    monthApts.forEach(apt => {
      if (!addedIds.has(apt.id)) {
        addedIds.add(apt.id);
        result.push(apt);
      }
    });
    
    // Also include multi-day appointments directly from week view (appointments prop)
    let weekAppointments = appointments || [];
    try {
      const storedAppointments = JSON.parse(userStorage.getItem('zielplanung_appointments') || '[]');
      if (storedAppointments.length > weekAppointments.length) {
        weekAppointments = storedAppointments;
      }
    } catch (e) {}
    
    // Filter to only include events that overlap with this month
    const monthStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const monthEnd = `${year}-${String(month + 1).padStart(2, '0')}-${getDaysInMonth(year, month)}`;
    
    weekAppointments.forEach(apt => {
      if (!apt.isMultiDay || !apt.endDate || apt.endDate === apt.date) return;
      // Exclude transferred items - they're shown from original source (month/year)
      if (apt.transferredFrom) return;
      // Check if event overlaps with this month
      if (!(apt.date <= monthEnd && apt.endDate >= monthStart)) return;
      
      if (!addedIds.has(apt.id)) {
        addedIds.add(apt.id);
        result.push({
          id: apt.id,
          title: apt.title,
          from: apt.date,
          to: apt.endDate,
          note: `${apt.startTime}-${apt.endTime}`,
          isWeekItem: true,
          transferredToWeek: true
        });
      }
    });
    
    return result.sort((a, b) => (a.id || 0) - (b.id || 0));
  };

  // Get color index for a multi-day item (consistent across all views)
  const getMultiDayColorIndex = (itemId) => {
    const multiDayItems = getMultiDayItems();
    const idx = multiDayItems.findIndex(item => item.id === itemId);
    return idx >= 0 ? idx : 0;
  };

  // Get appointments for a specific day (single-day items only)
  const getSingleDayAppointments = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    // Track which IDs have been added to avoid duplicates
    const addedIds = new Set();
    const result = [];
    
    // Check transferredIds for marking items as transferred - load early
    let transferredIds = [];
    try {
      transferredIds = JSON.parse(userStorage.getItem('zielplanung_transferred_ids') || '[]');
    } catch (e) {}
    
    // Regular appointments from Google Calendar etc (exclude multi-day and transferred items)
    // Transferred items are shown from their original source (month/year) with checkmark
    const regularApts = (appointments || []).filter(apt => {
      if (!apt.date) return false;
      // Exclude transferred items - they're shown from original source
      if (apt.transferredFrom) return false;
      // Exclude multi-day appointments - they're shown as spans
      if (apt.isMultiDay && apt.endDate && apt.endDate !== apt.date) return false;
      return apt.date === dateStr || apt.date.startsWith(dateStr);
    });
    
    regularApts.forEach(apt => {
      if (!addedIds.has(apt.id)) {
        addedIds.add(apt.id);
        result.push({
          ...apt,
          transferredToWeek: apt.transferredToWeek || transferredIds.includes(apt.id)
        });
      }
    });
    
    // Month appointments (single day only) - read from localStorage for most up-to-date data
    let monthApts = getMonthAppointments();
    try {
      const storedMonth = JSON.parse(userStorage.getItem('zielplanung_month_appointments') || '{}');
      const storedApts = storedMonth?.[yearMonthKey] || [];
      if (storedApts.length > 0) {
        monthApts = storedApts;
      }
    } catch (e) {}
    
    monthApts.filter(apt => apt.from === apt.to && apt.from === dateStr).forEach(apt => {
      if (!addedIds.has(apt.id)) {
        addedIds.add(apt.id);
        result.push({
          ...apt,
          transferredToWeek: apt.transferredToWeek || transferredIds.includes(apt.id)
        });
      }
    });
    
    // Single-day items from year planning - read from localStorage
    let yearItems = yearPlanning?.[yearMonthKey] || [];
    try {
      const storedYear = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
      const storedItems = storedYear?.[yearMonthKey] || [];
      if (storedItems.length > 0) {
        yearItems = storedItems;
      }
    } catch (e) {}
    
    yearItems.filter(item => item.from === item.to && item.from === dateStr).forEach(item => {
      // Skip year-only items (created from ZuErledigienModal → Jahresansicht)
      if (item.yearOnly) return;
      
      // Skip if already added (same id or year-prefixed id)
      if (addedIds.has(item.id) || addedIds.has(`year-${item.id}`)) return;
      
      // Skip if this item exists in monthApts with same title/date (duplicate check)
      const existsInMonth = monthApts.some(apt => 
        apt.id === item.id || 
        apt.originalYearId === item.id ||
        (apt.title === item.title && apt.from === item.from)
      );
      if (existsInMonth && item.transferred) return;
      
      addedIds.add(item.id);
      result.push({
        id: `year-${item.id}`,
        title: item.title || item.note,
        note: item.note,
        from: item.from,
        to: item.to,
        done: item.done || false,
        contactRef: item.contactRef || null,
        isYearPlanItem: true,
        priority: item.priority,
        transferredToWeek: item.transferredToWeek || transferredIds.includes(item.id),
        color: item.color
      });
    });
    
    return result;
  };

  // Check if a multi-day item spans this day
  const getSpansForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const multiDayItems = getMultiDayItems();
    
    return multiDayItems.filter(item => {
      const toDate = item.to || item.from;
      return dateStr >= item.from && dateStr <= toDate;
    }).map(item => ({
      ...item,
      isFirstDay: item.from === dateStr,
      isLastDay: item.to === dateStr,
      isMiddle: item.from !== dateStr && item.to !== dateStr
    }));
  };

  // Handle day click to open add modal
  const handleDayClickInternal = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDay(day);
    setEditingAppointment(null);
    setShowColorPicker(false);
    setNewAppointment({ title: '', from: dateStr, to: dateStr, note: '', priority: 5, color: '', contactRef: null });
    setContactSearch(''); setContactResults([]);
    setShowAddModal(true);
  };
  
  // Helper: check if a day in this month is in the past
  const isDayInPastMonth = (day) => {
    const d = new Date(year, month, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  };
  
  // Helper: check if a week (by its last day) is fully in the past
  const isWeekInPast = (weekDays) => {
    const lastDay = [...weekDays].reverse().find(d => d !== null);
    if (!lastDay) return true;
    const d = new Date(year, month, lastDay);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  };

  // Handle clicking on existing appointment (including transferred year plan items - all editable)
  const handleAppointmentClick = (apt, e) => {
    e.stopPropagation();
    setEditingAppointment(apt);
    setShowColorPicker(false);
    setNewAppointment({
      title: apt.title || apt.note || '',
      from: apt.from || apt.date || '',
      to: apt.to || apt.date || '',
      note: apt.note || '',
      priority: apt.priority || 5,
      color: apt.color || '',
      contactRef: apt.contactRef || null,
    });
    setContactSearch(apt.contactRef?.name || '');
    setContactResults([]);
    setShowAddModal(true);
  };

  // === TRANSFER TO WEEK FUNCTIONS ===
  
  // Get all items for a specific week (for transfer)
  const getWeekItems = (weekNumber, weekDays) => {
    const allItems = [];
    
    // Get list of already transferred item IDs
    let transferredIds = [];
    try {
      transferredIds = JSON.parse(userStorage.getItem('zielplanung_transferred_ids') || '[]');
    } catch (e) {}
    
    // Get month appointments for this week
    // Use localStorage directly to get most up-to-date data
    let monthApts = [];
    try {
      const stored = JSON.parse(userStorage.getItem('zielplanung_month_appointments') || '{}');
      monthApts = stored?.[yearMonthKey] || [];
    } catch (e) {
      monthApts = getMonthAppointments();
    }
    
    weekDays.forEach(day => {
      if (!day) return;
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      monthApts.forEach(apt => {
        if (apt.from === dateStr || (apt.from <= dateStr && apt.to >= dateStr)) {
          const isTransferred = apt.transferredToWeek || transferredIds.includes(apt.id) || transferredIds.includes(`month-${apt.id}`);
          if (!allItems.find(i => i.id === apt.id) && !isTransferred) {
            allItems.push({ ...apt, source: 'month' });
          }
        }
      });
    });
    
    // Get year planning items for this week
    // Use localStorage directly to get most up-to-date data
    let yearItems = [];
    try {
      const stored = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
      yearItems = stored?.[yearMonthKey] || [];
    } catch (e) {
      yearItems = yearPlanning?.[yearMonthKey] || [];
    }
    
    weekDays.forEach(day => {
      if (!day) return;
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      yearItems.forEach(item => {
        if (item.from === dateStr || (item.from <= dateStr && (item.to || item.from) >= dateStr)) {
          const isTransferred = item.transferredToWeek || transferredIds.includes(item.id) || transferredIds.includes(`year-${item.id}`);
          if (!allItems.find(i => i.id === item.id) && !isTransferred) {
            allItems.push({ ...item, source: 'year' });
          }
        }
      });
    });
    
    return allItems.sort((a, b) => (a.from || '').localeCompare(b.from || ''));
  };

  // Open transfer modal for a week
  const handleOpenTransferModal = (weekNumber, weekDays) => {
    const items = getWeekItems(weekNumber, weekDays);
    if (items.length === 0) {
      alert('Alle Einträge dieser Woche wurden bereits übertragen.');
      return;
    }
    setTransferWeekData({ weekNumber, weekDays });
    setTransferItems(items);
    setCurrentTransferIndex(0);
    setTransferAddedAppointments([]); // Reset added appointments tracking
    // Set initial time for first item (only for single-day appointments)
    if (items.length > 0) {
      const firstItem = items[0];
      const isMultiDay = firstItem.from !== firstItem.to;
      if (!isMultiDay) {
        const availableTime = findNextAvailableTime(firstItem.from, firstItem.startTime || '09:00');
        setTransferEditData({
          startTime: availableTime.startTime,
          endTime: availableTime.endTime
        });
      } else {
        // Multi-day: use default times
        setTransferEditData({
          startTime: firstItem.startTime || '09:00',
          endTime: firstItem.endTime || '10:00'
        });
      }
    } else {
      setTransferEditData({});
    }
    setTransferredInSession([]);
    setTransferOverlapError('');
    setShowTransferModal(true);
  };

  // Get current transfer item
  const getCurrentTransferItem = () => {
    if (!transferItems.length || currentTransferIndex >= transferItems.length) return null;
    const item = transferItems[currentTransferIndex];
    return { ...item, ...transferEditData };
  };

  // Update transfer edit data
  const updateTransferEditData = (field, value) => {
    if (field === 'endTime') {
      // Validate end time must be at least 15 minutes after start time (only for single-day)
      const item = getCurrentTransferItem();
      const isMultiDay = (transferEditData.to ?? item?.to) !== (transferEditData.from ?? item?.from);
      
      // Skip validation for multi-day appointments
      if (!isMultiDay) {
        const currentStartTime = transferEditData.startTime ?? item?.startTime ?? '09:00';
        const [startH, startM] = currentStartTime.split(':').map(Number);
        const [endH, endM] = value.split(':').map(Number);
        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;
        
        // End time must be at least 15 minutes after start time
        if (endMinutes < startMinutes + 15) {
          const minEndMinutes = startMinutes + 15;
          const minEndH = Math.floor(minEndMinutes / 60);
          const minEndM = minEndMinutes % 60;
          value = `${minEndH.toString().padStart(2, '0')}:${minEndM.toString().padStart(2, '0')}`;
        }
      }
    }
    setTransferEditData(prev => ({ ...prev, [field]: value }));
  };

  // Helper to add one hour to a time string
  const addOneHour = (timeStr) => {
    const [h, m] = timeStr.split(':').map(Number);
    const newH = Math.min(h + 1, 23);
    return `${newH.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  // Find next available time slot for a date
  const findNextAvailableTime = (dateStr, preferredStartTime = '09:00', additionalAppointments = []) => {
    const allAppointments = [...(appointments || []), ...additionalAppointments];
    const dayAppointments = allAppointments.filter(apt => apt.date === dateStr && !apt.isMultiDay);
    if (dayAppointments.length === 0) return { startTime: preferredStartTime, endTime: addOneHour(preferredStartTime) };
    const sorted = dayAppointments.sort((a, b) => {
      const [aH, aM] = (a.startTime || '09:00').split(':').map(Number);
      const [bH, bM] = (b.startTime || '09:00').split(':').map(Number);
      return (aH * 60 + aM) - (bH * 60 + bM);
    });
    const slotDuration = 60;
    const [prefH, prefM] = preferredStartTime.split(':').map(Number);
    let candidateStart = prefH * 60 + prefM;
    for (let attempt = 0; attempt < 48; attempt++) {
      const candidateEnd = candidateStart + slotDuration;
      if (candidateEnd > 23 * 60) break;
      let hasOverlap = false;
      for (const apt of sorted) {
        const [aptSH, aptSM] = (apt.startTime || '09:00').split(':').map(Number);
        const [aptEH, aptEM] = (apt.endTime || '10:00').split(':').map(Number);
        if (candidateStart < (aptEH * 60 + aptEM) && candidateEnd > (aptSH * 60 + aptSM)) {
          candidateStart = aptEH * 60 + aptEM;
          hasOverlap = true;
          break;
        }
      }
      if (!hasOverlap) {
        const sH = Math.floor(candidateStart / 60), sM = candidateStart % 60;
        const ns = `${sH.toString().padStart(2, '0')}:${sM.toString().padStart(2, '0')}`;
        return { startTime: ns, endTime: addOneHour(ns) };
      }
    }
    return { startTime: '09:00', endTime: '10:00' };
  };

  // Handle start time change - adjust end time if needed
  const handleTransferStartTimeChange = (newStartTime) => {
    const item = getCurrentTransferItem();
    const isMultiDay = (transferEditData.to ?? item?.to) !== (transferEditData.from ?? item?.from);
    
    // For multi-day: no validation, just set the value
    if (isMultiDay) {
      setTransferEditData(prev => ({ ...prev, startTime: newStartTime }));
      return;
    }
    
    const currentEndTime = transferEditData.endTime ?? item?.endTime ?? '10:00';
    const [startH, startM] = newStartTime.split(':').map(Number);
    const [endH, endM] = currentEndTime.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    
    // If end time is less than 15 minutes after new start time, adjust it
    if (endMinutes < startMinutes + 15) {
      const newEndHour = Math.min(startH + 1, 23);
      const newEndTime = `${newEndHour.toString().padStart(2, '0')}:${startM.toString().padStart(2, '0')}`;
      setTransferEditData(prev => ({ ...prev, startTime: newStartTime, endTime: newEndTime }));
    } else {
      setTransferEditData(prev => ({ ...prev, startTime: newStartTime }));
    }
  };

  // Handle "Weiter" - transfer to week view
  const handleTransferNext = () => {
    const item = getCurrentTransferItem();
    if (!item) return;
    
    const appointmentDate = transferEditData.from ?? item.from;
    const startTime = transferEditData.startTime ?? item.startTime ?? '09:00';
    const endTime = transferEditData.endTime ?? item.endTime ?? '10:00';
    const isMultiDay = (transferEditData.to ?? item.to) !== appointmentDate;
    
    // Clear previous error
    setTransferOverlapError('');
    
    // Check for overlaps in week view (only for single-day appointments)
    // Include both existing appointments AND appointments added in this transfer session
    if (!isMultiDay) {
      const allAppointments = [...(appointments || []), ...transferAddedAppointments];
      const dayAppointments = allAppointments.filter(apt => 
        apt.date === appointmentDate && !apt.isMultiDay
      );
      
      for (const apt of dayAppointments) {
        const [newStartH, newStartM] = startTime.split(':').map(Number);
        const [newEndH, newEndM] = endTime.split(':').map(Number);
        const [aptStartH, aptStartM] = (apt.startTime || '00:00').split(':').map(Number);
        const [aptEndH, aptEndM] = (apt.endTime || '23:59').split(':').map(Number);
        
        const newStart = newStartH * 60 + newStartM;
        const newEnd = newEndH * 60 + newEndM;
        const aptStart = aptStartH * 60 + aptStartM;
        const aptEnd = aptEndH * 60 + aptEndM;
        
        if (newStart < aptEnd && newEnd > aptStart) {
          setTransferOverlapError(`Überschneidung mit "${apt.title}" (${apt.startTime}-${apt.endTime})`);
          return;
        }
      }
    }
    
    // Create appointment for week view
    // Match the exact format used by AppointmentModal in WeekView
    // Store original IDs for cascading delete functionality
    const weekAppointment = {
      id: Date.now(),
      title: transferEditData.title ?? item.title ?? '',
      date: appointmentDate,  // YYYY-MM-DD format
      endDate: isMultiDay ? (transferEditData.to ?? item.to) : null,
      isMultiDay: isMultiDay,
      startTime: startTime,
      endTime: endTime,
      priority: transferEditData.priority ?? item.priority ?? 5,
      color: transferEditData.color ?? item.color ?? null,
      note: transferEditData.note || null,
      transferredFrom: item.source,
      // Store original IDs for cascading delete
      originalMonthId: item.source === 'month' ? item.id : (item.originalMonthId || null),
      originalYearId: item.source === 'year' ? item.id : (item.originalYearId || null),
      targetCalendar: null,
      googleEventId: null,
      exportedToCalendar: null
    };
    
    // Add to week appointments via callback
    if (onTransferToWeek) {
      onTransferToWeek(weekAppointment);
    }
    
    // Track this appointment for time calculation
    setTransferAddedAppointments(prev => [...prev, weekAppointment]);
    
    // Mark original item as transferred
    markItemAsTransferred(item);
    
    setTransferredInSession(prev => [...prev, item.id]);
    
    // Move to next or close
    if (currentTransferIndex + 1 >= transferItems.length) {
      finishTransfer();
    } else {
      const nextIndex = currentTransferIndex + 1;
      const nextItem = transferItems[nextIndex];
      const isNextMultiDay = nextItem.from !== nextItem.to;
      
      // Only calculate next available time for single-day appointments
      if (!isNextMultiDay) {
        const nextAvailable = findNextAvailableTime(nextItem.from, nextItem.startTime || '09:00', [...transferAddedAppointments, weekAppointment]);
        setTransferEditData({
          startTime: nextAvailable.startTime,
          endTime: nextAvailable.endTime
        });
      } else {
        // Multi-day: use default times
        setTransferEditData({
          startTime: nextItem.startTime || '09:00',
          endTime: nextItem.endTime || '10:00'
        });
      }
      setCurrentTransferIndex(nextIndex);
      setTransferOverlapError('');
    }
  };

  // Handle "Überspringen"
  const handleTransferSkip = () => {
    if (currentTransferIndex + 1 >= transferItems.length) {
      finishTransfer();
    } else {
      const nextIndex = currentTransferIndex + 1;
      const nextItem = transferItems[nextIndex];
      const isNextMultiDay = nextItem.from !== nextItem.to;
      
      // Only calculate next available time for single-day appointments
      if (!isNextMultiDay) {
        const nextAvailable = findNextAvailableTime(nextItem.from, nextItem.startTime || '09:00', transferAddedAppointments);
        setTransferEditData({
          startTime: nextAvailable.startTime,
          endTime: nextAvailable.endTime
        });
      } else {
        // Multi-day: use default times
        setTransferEditData({
          startTime: nextItem.startTime || '09:00',
          endTime: nextItem.endTime || '10:00'
        });
      }
      setCurrentTransferIndex(nextIndex);
      setTransferOverlapError('');
    }
  };

  // Handle "To-dos"
  const handleTransferToTodo = () => {
    const item = getCurrentTransferItem();
    if (!item) return;
    
    // Add to weekly todos
    if (window.addToWeeklyTodos) {
      window.addToWeeklyTodos({
        id: Date.now(),
        text: transferEditData.title ?? item.title ?? item.note ?? '',
        priority: transferEditData.priority ?? item.priority ?? 5,
        dueDate: transferEditData.from ?? item.from,
        done: false,
        createdAt: new Date().toISOString(),
        sourceId: item.id
      });
    }
    
    // Mark as transferred
    markItemAsTransferred(item);
    
    if (currentTransferIndex + 1 >= transferItems.length) {
      finishTransfer();
    } else {
      setCurrentTransferIndex(prev => prev + 1);
      setTransferEditData({});
    }
  };

  // Mark item as transferred in its source
  const markItemAsTransferred = (item) => {
    // Save transferred ID to separate localStorage key for reliable filtering
    try {
      const transferredIds = JSON.parse(userStorage.getItem('zielplanung_transferred_ids') || '[]');
      const itemKey = `${item.source}-${item.id}`;
      if (!transferredIds.includes(item.id) && !transferredIds.includes(itemKey)) {
        transferredIds.push(item.id);
        transferredIds.push(itemKey);
        // Also add originalYearId if present (for items transferred from year to month)
        if (item.originalYearId) {
          transferredIds.push(item.originalYearId);
          transferredIds.push(`year-${item.originalYearId}`);
        }
        userStorage.setItem('zielplanung_transferred_ids', JSON.stringify(transferredIds));
      }
    } catch (e) {}
    
    if (item.source === 'month') {
      // Read directly from localStorage for most up-to-date data
      let currentApts = [];
      try {
        const stored = JSON.parse(userStorage.getItem('zielplanung_month_appointments') || '{}');
        currentApts = stored?.[yearMonthKey] || [];
      } catch (e) {
        currentApts = getMonthAppointments();
      }
      
      // Find the month item to check if it came from year (has originalYearId)
      const monthItem = currentApts.find(apt => apt.id === item.id);
      const linkedYearId = monthItem?.originalYearId || item.originalYearId;
      
      const updatedApts = currentApts.map(apt => 
        apt.id === item.id ? { ...apt, transferredToWeek: true } : apt
      );
      
      // Save directly to localStorage AND update state
      try {
        const allMonthApts = JSON.parse(userStorage.getItem('zielplanung_month_appointments') || '{}');
        allMonthApts[yearMonthKey] = updatedApts;
        userStorage.setItem('zielplanung_month_appointments', JSON.stringify(allMonthApts));
      } catch (e) {}
      
      onMonthAppointmentsChange(yearMonthKey, updatedApts);
      
      // Also mark year item as transferred to week
      // Match by: originalYearId (best), same ID, or title+from date
      try {
        const storedYear = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
        const yearItems = storedYear?.[yearMonthKey] || [];
        const updatedYearItems = yearItems.map(yi => {
          // Priority matching: 1) originalYearId, 2) same ID, 3) title+from
          const matches = 
            (linkedYearId && yi.id === linkedYearId) ||
            yi.id === item.id || 
            (yi.title === item.title && yi.from === item.from) ||
            (monthItem && yi.title === monthItem.title && yi.from === monthItem.from);
          return matches ? { ...yi, transferredToWeek: true } : yi;
        });
        storedYear[yearMonthKey] = updatedYearItems;
        userStorage.setItem('zielplanung_year_planning', JSON.stringify(storedYear));
        if (onYearPlanningChange) {
          onYearPlanningChange(yearMonthKey, updatedYearItems);
        }
      } catch (e) {}
    } else if (item.source === 'year') {
      // Read directly from localStorage for most up-to-date data
      let yearItems = [];
      try {
        const stored = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
        yearItems = stored?.[yearMonthKey] || [];
      } catch (e) {
        yearItems = yearPlanning?.[yearMonthKey] || [];
      }
      
      const updatedItems = yearItems.map(i => 
        i.id === item.id ? { ...i, transferredToWeek: true } : i
      );
      
      // Save directly to localStorage AND update state
      try {
        const allYearPlanning = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
        allYearPlanning[yearMonthKey] = updatedItems;
        userStorage.setItem('zielplanung_year_planning', JSON.stringify(allYearPlanning));
      } catch (e) {}
      
      if (onYearPlanningChange) {
        onYearPlanningChange(yearMonthKey, updatedItems);
      }
      
      // Also mark any corresponding month item as transferred to week
      // Match by originalYearId (if month item was transferred from year) or title+from
      try {
        const storedMonth = JSON.parse(userStorage.getItem('zielplanung_month_appointments') || '{}');
        const monthApts = storedMonth?.[yearMonthKey] || [];
        const updatedMonthApts = monthApts.map(apt => {
          const matches = 
            apt.originalYearId === item.id ||
            apt.id === item.id || 
            (apt.title === item.title && apt.from === item.from);
          return matches ? { ...apt, transferredToWeek: true } : apt;
        });
        storedMonth[yearMonthKey] = updatedMonthApts;
        userStorage.setItem('zielplanung_month_appointments', JSON.stringify(storedMonth));
        onMonthAppointmentsChange(yearMonthKey, updatedMonthApts);
      } catch (e) {}
    }
  };

  // Handle "Abbrechen"
  const handleTransferCancel = () => {
    setShowTransferModal(false);
    setTransferWeekData(null);
    setTransferItems([]);
    setCurrentTransferIndex(0);
    setTransferEditData({});
    setTransferredInSession([]);
    setTransferOverlapError('');
    setTransferAddedAppointments([]);
  };

  // Finish transfer
  const finishTransfer = () => {
    setShowTransferModal(false);
    setTransferWeekData(null);
    setTransferItems([]);
    setCurrentTransferIndex(0);
    setTransferEditData({});
    setTransferOverlapError('');
    setTransferredInSession([]);
    setTransferAddedAppointments([]);
  };

  // === END TRANSFER FUNCTIONS ===

  // Priority color helper
  const getPriorityColor = (priority) => {
    if (priority >= 8) return 'bg-red-100 text-red-700';
    if (priority >= 5) return 'bg-amber-100 text-amber-700';
    return 'bg-green-100 text-green-700';
  };

  // Priority border color (for left border indicator)
  const getPriorityBorderColor = (priority) => {
    if (priority >= 8) return 'border-red-500';
    if (priority >= 5) return 'border-amber-500';
    return 'border-green-500';
  };

  // Span bar colors (random colors for multi-day events)
  const spanColors = ['bg-blue-400', 'bg-purple-400', 'bg-orange-400', 'bg-pink-400', 'bg-cyan-400'];

  const handleSaveAppointment = () => {
    if (!newAppointment.title.trim()) return;
    
    const currentApts = getMonthAppointments();
    
    // Check if editing a transferred year item
    const isYearItem = editingAppointment && (
      editingAppointment.isYearPlanItem || 
      editingAppointment.isYearItem || 
      (editingAppointment.id && String(editingAppointment.id).startsWith('year-'))
    );
    
    if (editingAppointment) {
      if (isYearItem && onYearPlanningChange) {
        // Update in BOTH year planning and month view
        const originalId = String(editingAppointment.id).startsWith('year-') 
          ? parseInt(String(editingAppointment.id).replace('year-', '')) 
          : editingAppointment.id;
        
        // Update year planning
        const yearItems = yearPlanning?.[yearMonthKey] || [];
        const updatedYearItems = yearItems.map(item => 
          item.id === originalId 
            ? { ...item, title: newAppointment.title, note: newAppointment.note, from: newAppointment.from, to: newAppointment.to, priority: newAppointment.priority }
            : item
        );
        onYearPlanningChange(yearMonthKey, updatedYearItems);
        
        // Also update in month appointments if it exists there
        const monthItem = currentApts.find(apt => apt.id === editingAppointment.id);
        if (monthItem) {
          const updatedMonthApts = currentApts.map(apt => 
            apt.id === editingAppointment.id ? { ...newAppointment, id: editingAppointment.id } : apt
          );
          onMonthAppointmentsChange(yearMonthKey, updatedMonthApts);
        }
      } else {
        // Regular month appointment - update only month
        const updated = currentApts.map(apt => 
          apt.id === editingAppointment.id ? { ...newAppointment, id: editingAppointment.id } : apt
        );
        onMonthAppointmentsChange(yearMonthKey, updated);
      }
    } else {
      // Add new
      const newApt = {
        id: Date.now(),
        ...newAppointment
      };
      onMonthAppointmentsChange(yearMonthKey, [...currentApts, newApt]);
    }

    // Fire transfer-complete event so TodosView deletes the source todo
    if (pendingTransferTodo) {
      window.dispatchEvent(new CustomEvent('burningteam-transfer-complete', { detail: pendingTransferTodo }));
      setPendingTransferTodo(null);
    }
    
    setShowAddModal(false);
    setEditingAppointment(null);
    setShowColorPicker(false);
    setNewAppointment({ title: '', from: '', to: '', note: '', priority: 5, color: '', contactRef: null });
    setContactSearch('');
    setContactResults([]);
  };

  const handleDeleteAppointment = () => {
    if (!editingAppointment) return;
    
    // Show delete confirmation within the same modal
    setShowDeleteConfirm(true);
  };
  
  // Execute the actual delete
  const executeDelete = () => {
    if (!editingAppointment) return;
    
    // Get the real ID - remove 'year-' prefix if present
    let itemId = editingAppointment.id;
    let isYearItem = false;
    
    if (typeof itemId === 'string' && itemId.startsWith('year-')) {
      itemId = parseInt(itemId.replace('year-', ''));
      isYearItem = true;
    }
    
    // Also check if this is a year item by other properties
    isYearItem = isYearItem || editingAppointment.isYearPlanItem || editingAppointment.isYearItem || editingAppointment.source === 'year';
    
    if (onDeleteFromAll) {
      // Pass the clean ID to the delete function
      onDeleteFromAll(itemId, yearMonthKey);
    }
    
    // If it's a year item, also directly delete from yearPlanning to be sure
    if (isYearItem && onYearPlanningChange) {
      try {
        const stored = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
        const yearItems = stored?.[yearMonthKey] || [];
        const updatedItems = yearItems.filter(item => item.id !== itemId);
        stored[yearMonthKey] = updatedItems;
        userStorage.setItem('zielplanung_year_planning', JSON.stringify(stored));
        onYearPlanningChange(yearMonthKey, updatedItems);
      } catch (e) {}
    }
    
    setShowDeleteConfirm(false);
    setShowAddModal(false);
    setEditingAppointment(null);
  };

  // Delete transferred item - with option to also delete from year plan
  // Get week number for a date
  const getWeekNumberForDate = (dateObj) => {
    const d = new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const handleSaveGoal = () => {
    onMonthGoalChange(monthKey, goalText);
    setEditingGoal(false);
  };

  const handleAddTask = () => {
    if (!newTask.trim()) return;
    const currentTasks = tasks[monthKey] || [];
    onTaskChange(monthKey, [...currentTasks, { id: Date.now(), text: newTask, done: false }]);
    setNewTask('');
  };

  const toggleTask = (taskId) => {
    const currentTasks = tasks[monthKey] || [];
    onTaskChange(monthKey, currentTasks.map(t => t.id === taskId ? { ...t, done: !t.done } : t));
  };

  const deleteTask = (taskId) => {
    const currentTasks = tasks[monthKey] || [];
    onTaskChange(monthKey, currentTasks.filter(t => t.id !== taskId));
  };

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let day = 1; day <= daysInMonth; day++) calendarDays.push(day);
  // Fill remaining cells to complete the grid
  while (calendarDays.length % 7 !== 0) calendarDays.push(null);

  // Group calendar days into weeks with week numbers
  const weeks = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    const weekDays = calendarDays.slice(i, i + 7);
    // Find first actual day in this week to calculate week number
    const firstDayInWeek = weekDays.find(d => d !== null) || 1;
    const dateForWeek = new Date(year, month, firstDayInWeek);
    const weekNum = getWeekNumberForDate(dateForWeek);
    weeks.push({ days: weekDays, weekNumber: weekNum });
  }

  // Get multi-day items for rendering spans
  const multiDayItems = getMultiDayItems();
  
  // Get all appointments for a specific week
  const getWeekAppointments = (weekData) => {
    const weekAppts = [];
    const yearMonthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
    
    // Read from localStorage for most up-to-date data
    let allMonthAppts = monthAppointments?.[yearMonthKey] || [];
    try {
      const stored = JSON.parse(userStorage.getItem('zielplanung_month_appointments') || '{}');
      const storedApts = stored?.[yearMonthKey] || [];
      if (storedApts.length > 0) {
        allMonthAppts = storedApts;
      }
    } catch (e) {}
    
    let yearItems = yearPlanning?.[yearMonthKey] || [];
    try {
      const stored = JSON.parse(userStorage.getItem('zielplanung_year_planning') || '{}');
      const storedItems = stored?.[yearMonthKey] || [];
      if (storedItems.length > 0) {
        yearItems = storedItems;
      }
    } catch (e) {}
    
    // Track which IDs have been added to avoid duplicates
    const addedIds = new Set();
    
    weekData.days.forEach(day => {
      if (!day) return;
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      // From monthAppointments - check by 'from' date field
      allMonthAppts.forEach(apt => {
        // Check if this appointment falls on this day (by 'from' field)
        if (apt.from === dateStr || (apt.from && apt.to && apt.from <= dateStr && apt.to >= dateStr)) {
          if (!addedIds.has(apt.id)) {
            addedIds.add(apt.id);
            weekAppts.push({ ...apt, day, dateStr: apt.from || dateStr, source: 'month' });
          }
        }
      });
      
      // From yearPlanning - check by 'from' date field
      // Skip items that are already in monthAppointments (to avoid duplicates)
      yearItems.forEach(item => {
        // Skip if already transferred to month AND exists in monthAppts
        if (item.transferred) {
          const existsInMonth = allMonthAppts.some(apt => 
            apt.id === item.id || 
            apt.originalYearId === item.id ||
            (apt.title === item.title && apt.from === item.from)
          );
          if (existsInMonth) return;
        }
        
        const itemFrom = item.from || '';
        const itemTo = item.to || itemFrom;
        if (itemFrom === dateStr || (itemFrom && itemTo && itemFrom <= dateStr && itemTo >= dateStr)) {
          if (!addedIds.has(item.id) && !addedIds.has(`year-${item.id}`)) {
            addedIds.add(item.id);
            weekAppts.push({ ...item, day, dateStr: itemFrom || dateStr, source: 'year' });
          }
        }
      });
      // Also include habit reminder entries from appointments prop (per day)
      (appointments || []).filter(apt => apt.isHabitReminder && apt.date === dateStr).forEach(apt => {
        if (!addedIds.has(apt.id)) {
          addedIds.add(apt.id);
          weekAppts.push({ ...apt, from: apt.date, to: apt.date, day, dateStr: apt.date, source: 'habit', completed: apt.completed });
        }
      });
      // Also include normal week-view appointments (single-day, non-habit, non-multi-day)
      // Source of truth: appointments prop PLUS zielplanung_appointments fallback
      let weekAppointmentsSource = appointments || [];
      try {
        const storedWeekApts = JSON.parse(userStorage.getItem('zielplanung_appointments') || '[]');
        if (storedWeekApts.length > weekAppointmentsSource.length) {
          weekAppointmentsSource = storedWeekApts;
        }
      } catch (e) {}
      weekAppointmentsSource
        .filter(apt => !apt.isHabitReminder && !apt.isMultiDay && apt.date === dateStr)
        .forEach(apt => {
          if (!addedIds.has(apt.id)) {
            addedIds.add(apt.id);
            weekAppts.push({
              ...apt,
              from: apt.date,
              to: apt.date,
              day,
              dateStr: apt.date,
              source: 'week',
              note: apt.note || (apt.startTime ? `${apt.startTime}-${apt.endTime}` : ''),
            });
          }
        });
    });
    
    // Sort by date then time
    return weekAppts.sort((a, b) => {
      const dateA = a.from || a.dateStr || '';
      const dateB = b.from || b.dateStr || '';
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      return (a.startTime || '00:00').localeCompare(b.startTime || '00:00');
    });
  };

  // List Mode View
  if (viewMode === 'list') {
    return (
      <div className="space-y-4">
        {/* Kompakter Header: Monatsziel + Monats-Aufgaben */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
            {/* Monatsziel - einzeilig, nicht editierbar */}
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <Target className="w-4 h-4 text-blue-600 shrink-0" />
              <span className="text-sm font-medium text-slate-700 shrink-0">Monatsziel:</span>
              {wizardData?.monthGoals?.[yearMonthKey] ? (
                <span className={`text-sm truncate flex-1 min-w-0 ${wizardData.monthGoals[yearMonthKey].done ? 'line-through text-slate-400' : 'text-slate-600'}`}>
                  {wizardData.monthGoals[yearMonthKey].done && <span className="text-emerald-600 mr-1">✓</span>}
                  {wizardData.monthGoals[yearMonthKey].number} {wizardData.monthGoals[yearMonthKey].text}
                </span>
              ) : (
                <span className="text-sm text-slate-400 italic">Nicht geplant</span>
              )}
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px h-6 bg-slate-200 shrink-0"></div>

            <div className="flex items-center justify-between w-full sm:w-auto sm:justify-end sm:gap-1">
            {/* Monats-Aufgaben */}
            <div 
              onClick={() => setShowMonthTodosModal(true)}
              className="flex items-center gap-1 sm:gap-2 cursor-pointer hover:bg-purple-50 rounded-lg px-1 py-0.5 sm:px-2 sm:py-1 transition-colors"
            >
              <ListTodo className="w-4 h-4 text-purple-600 flex-shrink-0" />
              <span className="text-sm font-medium text-slate-700 whitespace-nowrap">Aufgaben:</span>
              {(() => {
                const grouped = getMonthTodosGrouped();
                let total = 0, done = 0;
                Object.values(grouped).forEach(todos => {
                  total += todos.length;
                  done += todos.filter(t => t.done).length;
                });
                return (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">{total - done} offen</span>
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">{done} ✓</span>
                  </div>
                );
              })()}
            </div>
            
            {/* Neuer Termin Button - ganz rechts */}
            <button 
              onClick={() => {
                const now = new Date();
                const todayDay = now.getDate();
                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(todayDay).padStart(2, '0')}`;
                setSelectedDay(todayDay);
                setEditingAppointment(null);
                setShowColorPicker(false);
                setNewAppointment({ title: '', from: dateStr, to: dateStr, note: '', priority: 5, color: '' });
                setShowAddModal(true);
              }}
              className="w-7 h-7 text-sm font-bold text-white bg-amber-500 hover:bg-amber-600 rounded transition-colors flex items-center justify-center"
              title="Neuer Termin"
              data-testid="month-calendar-new-appointment-btn"
            >
              <Plus className="w-4 h-4" />
            </button>
            </div>
          </div>
        </div>

        {/* Monatsziel Edit Modal */}
        {editingGoal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-600" />
                  Monatsziel - {['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'][month]} {year}
                </h3>
                <button onClick={() => setEditingGoal(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <textarea 
                value={goalText} 
                onChange={(e) => setGoalText(e.target.value)} 
                placeholder="Was möchtest du diesen Monat erreichen?" 
                className="w-full p-3 border border-slate-200 rounded-lg text-sm" 
                rows={4} 
                autoFocus 
              />
              <div className="flex gap-2 mt-4">
                <Button onClick={handleSaveGoal} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Speichern</Button>
                <Button onClick={() => setEditingGoal(false)} variant="outline">Abbrechen</Button>
              </div>
            </div>
          </div>
        )}

        {/* Week List */}
        {weeks.map((weekData, weekIndex) => {
          const weekAppts = getWeekAppointments(weekData);
          const firstDay = weekData.days.find(d => d !== null);
          const lastDay = [...weekData.days].reverse().find(d => d !== null);
          
          return (
            <div key={weekIndex} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between bg-slate-50 px-4 py-2 border-b border-slate-200">
                <h3 className="font-semibold text-slate-800">KW {weekData.weekNumber}</h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">
                    {firstDay}.{month + 1}. - {lastDay}.{month + 1}. ({weekAppts.length} Termine)
                  </span>
                  {/* Action buttons */}
                  <div className="flex gap-1">
                    <button 
                      className="w-7 h-7 text-sm font-bold text-white bg-blue-500 hover:bg-blue-600 rounded transition-colors flex items-center justify-center"
                      onClick={() => handleOpenTransferModal(weekData.weekNumber, weekData.days)}
                      title="Übertragen"
                    >
                      →
                    </button>
                    {(() => {
                      const grouped = getMonthTodosGrouped();
                      const kwTodos = grouped[weekData.weekNumber] || [];
                      const openCount = kwTodos.filter(t => !t.done).length;
                      return (
                        <button 
                          className={`relative h-7 text-sm font-bold text-white rounded transition-colors flex items-center justify-center gap-1 px-1.5 ${openCount > 0 ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'}`}
                          onClick={() => openMonthTodosForKW(weekData.weekNumber)}
                          title="KW-Aufgaben"
                        >
                          <ListTodo className="w-3.5 h-3.5" />
                          {openCount > 0 && <span className="text-[10px] font-bold">{openCount}</span>}
                        </button>
                      );
                    })()}
                    <button 
                      className="w-7 h-7 text-sm font-bold text-white bg-emerald-500 hover:bg-emerald-600 rounded transition-colors flex items-center justify-center"
                      onClick={() => onGoToWeek && onGoToWeek(weekData.weekNumber, weekData.days)}
                      title="zur Woche"
                    >
                      ▶
                    </button>
                    <button 
                      className="w-7 h-7 text-sm font-bold text-white bg-amber-500 hover:bg-amber-600 rounded transition-colors flex items-center justify-center"
                      onClick={() => {
                        // Open add modal for first day of this week
                        const firstDayOfWeek = weekData.days.find(d => d !== null);
                        if (firstDayOfWeek) {
                          const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(firstDayOfWeek).padStart(2, '0')}`;
                          setNewAppointment({
                            title: '',
                            from: dateKey,
                            to: dateKey,
                            note: '',
                            priority: 5,
                            color: ''
                          });
                          setEditingAppointment(null);
                          setShowAddModal(true);
                        }
                      }}
                      title="Neuer Termin"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              {weekAppts.length === 0 ? (
                <div className="p-3 text-center text-slate-400 text-sm">Keine Termine</div>
              ) : (
                <div className="space-y-1 p-1">
                  {weekAppts.map((apt, idx) => {
                    const aptDateStr = apt.from || apt.dateStr || '';
                    const aptDate = aptDateStr ? new Date(aptDateStr) : null;
                    const dayNum = aptDate ? aptDate.getDate() : apt.day;
                    const dayName = aptDate ? DAYS_SHORT[aptDate.getDay() === 0 ? 6 : aptDate.getDay() - 1] : '';
                    
                    // Format date range for multi-day items
                    const isMultiDay = apt.from && apt.to && apt.from !== apt.to;
                    const fromDay = apt.from ? new Date(apt.from).getDate() : dayNum;
                    const toDay = apt.to ? new Date(apt.to).getDate() : dayNum;
                    
                    // Priority border color
                    const priority = apt.priority || 5;
                    const priorityBorderColor = priority >= 8 ? 'border-l-red-500' : 
                      priority >= 5 ? 'border-l-amber-500' : 'border-l-emerald-500';
                    
                    // Get consistent color based on appointment - use multiDayIndex for consistent colors
                    const colorIndex = isMultiDay ? getMultiDayColorIndex(apt.id) : 0;
                    const aptColor = getAppointmentColor(apt, colorIndex);
                    
                    return (
                      <div
                        key={apt.id || idx}
                        onClick={() => {
                          setEditingAppointment(apt);
                          setNewAppointment({
                            title: apt.title || '',
                            from: apt.from || '',
                            to: apt.to || apt.from || '',
                            note: apt.note || '',
                            priority: apt.priority || 5,
                            color: apt.color || ''
                          });
                          setShowAddModal(true);
                        }}
                        className={`p-2 cursor-pointer hover:opacity-80 border-l-4 ${aptColor.light} rounded-r shadow-sm ${priorityBorderColor} ${apt.transferredToWeek ? 'opacity-60' : ''} ${apt.done ? 'opacity-70' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium text-sm ${aptColor.lightText}`}>
                              {apt.transferredToWeek && <span className="mr-1">✓</span>}
                              {apt.done && !apt.transferredToWeek && <span className="mr-1">☑</span>}
                              {apt.isHabitReminder && apt.completed && <span className="mr-1 text-emerald-600">✓</span>}
                              <span className={`${apt.isHabitReminder && apt.completed ? 'line-through opacity-70' : ''} ${apt.done && !apt.isHabitReminder ? 'line-through' : ''}`}>{apt.title || apt.note || 'Ohne Titel'}</span>
                            </div>
                            {apt.note && apt.title && (
                              <div className="text-xs text-slate-500 truncate">{apt.note}</div>
                            )}
                            {apt.contactRef?.name && (
                              <div className="text-xs text-emerald-600 font-medium truncate flex items-center gap-1"><User className="w-3 h-3" />{apt.contactRef.name}</div>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 whitespace-nowrap">
                            {isMultiDay ? `${fromDay}.-${toDay}.` : `${dayName} ${dayNum}.`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Add/Edit Appointment Modal for List View */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">
                  {editingAppointment ? 'Termin bearbeiten' : 'Neuer Termin'}
                </h3>
                <button onClick={() => { setShowAddModal(false); setEditingAppointment(null); }} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Titel</label>
                  <input
                    type="text"
                    value={newAppointment.title}
                    onChange={(e) => setNewAppointment({...newAppointment, title: e.target.value})}
                    placeholder="Termintitel..."
                    className="w-full p-2 border border-slate-200 rounded-lg mt-1"
                    autoFocus
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Von</label>
                    <input
                      type="date"
                      value={newAppointment.from}
                      onChange={(e) => setNewAppointment({...newAppointment, from: e.target.value, to: newAppointment.to || e.target.value})}
                      className="w-full p-2 border border-slate-200 rounded-lg mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Bis</label>
                    <input
                      type="date"
                      value={newAppointment.to}
                      onChange={(e) => setNewAppointment({...newAppointment, to: e.target.value})}
                      className="w-full p-2 border border-slate-200 rounded-lg mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700">Notiz</label>
                  <textarea
                    value={newAppointment.note}
                    onChange={(e) => setNewAppointment({...newAppointment, note: e.target.value})}
                    placeholder="Zusätzliche Notizen..."
                    className="w-full p-2 border border-slate-200 rounded-lg mt-1"
                    rows={2}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700">Priorität: {newAppointment.priority || 5}</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newAppointment.priority || 5}
                    onChange={(e) => setNewAppointment({...newAppointment, priority: parseInt(e.target.value)})}
                    className="w-full mt-1"
                  />
                </div>

                {/* Kontakt-Verknüpfung */}
                <div ref={contactSearchRef} className="relative">
                  <label className="text-sm font-medium text-slate-700">Kontakt (optional)</label>
                  {newAppointment.contactRef ? (
                    <div className="flex items-center gap-2 mt-1 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <User className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <span className="text-sm text-emerald-700 font-medium flex-1">{newAppointment.contactRef.name}</span>
                      <button type="button" onClick={() => { setNewAppointment({...newAppointment, contactRef: null}); setContactSearch(''); }} className="text-emerald-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <>
                      <div className="relative mt-1">
                        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                        <input type="text" value={contactSearch} onChange={e => { setContactSearch(e.target.value); setContactSearchOpen(true); }}
                          onFocus={() => setContactSearchOpen(true)} placeholder="Kontakt suchen..."
                          className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                      </div>
                      {contactSearchOpen && contactResults.length > 0 && (
                        <div className="absolute top-full left-0 right-0 z-50 bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                          {contactResults.map(c => (
                            <button key={c._id || c.id} type="button" onMouseDown={e => e.preventDefault()}
                              onClick={() => { setNewAppointment({...newAppointment, contactRef: { id: c._id || c.id, typ: 'teampartner', name: c.name }}); setContactSearch(''); setContactSearchOpen(false); }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-emerald-50 flex items-center gap-2">
                              <User className="w-3.5 h-3.5 text-slate-400" />{c.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button 
                    onClick={handleSaveAppointment} 
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {editingAppointment ? 'Speichern' : 'Hinzufügen'}
                  </Button>
                  <Button onClick={() => { setShowAddModal(false); setEditingAppointment(null); setContactSearch(''); }} variant="outline">
                    Abbrechen
                  </Button>
                </div>
                {editingAppointment?.id && editingAppointment.contactRef && (
                  <button
                    type="button"
                    onClick={() => handleToggleMonthDone(editingAppointment)}
                    className={`w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all mt-1 ${
                      editingAppointment.done
                        ? 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                    }`}
                  >
                    <Check className="w-4 h-4" />
                    {editingAppointment.done ? 'Als offen markieren' : 'Erledigt'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Monats-Aufgaben Modal for List View */}
        {showMonthTodosModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`bg-white rounded-xl p-4 shadow-xl max-h-[85vh] overflow-hidden flex flex-col ${focusedKW ? 'max-w-md w-full' : 'max-w-4xl w-full'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <ListTodo className="w-5 h-5 text-purple-600" />
                  {focusedKW 
                    ? `KW ${focusedKW} Aufgaben`
                    : `Monats-Aufgaben - ${['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'][month]} ${year}`
                  }
                </h3>
                <button onClick={() => { setShowMonthTodosModal(false); setFocusedKW(null); }} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Single KW View (when opened from calendar) */}
              {focusedKW && (() => {
                const week = getWeeksInMonth().find(w => w.weekNum === focusedKW);
                if (!week) return null;
                
                const grouped = getMonthTodosGrouped();
                const todos = grouped[week.weekNum] || [];
                const openTodosCount = todos.filter(t => !t.done).length;
                const doneTodosCount = todos.filter(t => t.done).length;
                
                return (
                  <div className="flex-1 overflow-y-auto" key={monthTodosRefresh}>
                    <div className={`rounded-xl border-2 p-4 ${week.isCurrent ? 'border-emerald-400 bg-emerald-50' : 'border-purple-400 bg-purple-50'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-lg font-bold ${week.isCurrent ? 'text-emerald-700' : 'text-purple-700'}`}>
                            KW {week.weekNum}
                          </span>
                          {week.isCurrent && <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full">Aktuell</span>}
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          {openTodosCount > 0 && <span className="bg-purple-200 text-purple-700 px-2 py-1 rounded-full">{openTodosCount} offen</span>}
                          {doneTodosCount > 0 && <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">✓ {doneTodosCount}</span>}
                        </div>
                      </div>
                      
                      <p className="text-sm text-slate-500 mb-4">{formatDateRange(week)}</p>
                      
                      <div className="space-y-2 mb-4 max-h-[300px] overflow-y-auto">
                        {todos.length === 0 ? (
                          <p className="text-slate-400 text-sm text-center py-4">Keine Aufgaben für diese Woche</p>
                        ) : (
                          todos.map(todo => (
                            <div key={todo.id} className={`p-3 rounded-lg flex items-center gap-3 ${todo.done ? 'bg-white/50 text-slate-400' : 'bg-white text-slate-700'}`}>
                              <button onClick={() => toggleMonthTodo(todo.id, todo._fromWeekly)} className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${todo.done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 hover:border-emerald-400'}`}>
                                {todo.done && <Check className="w-3 h-3 text-white" />}
                              </button>
                              <button onClick={() => onEditTodo && onEditTodo(todo, 'monthly', { monthKey: yearMonthKey })} className={`flex-1 text-left text-sm ${todo.done ? 'line-through text-slate-400' : 'text-slate-700 hover:text-purple-700'}`}>{todo.text}</button>
                              <button onClick={() => deleteMonthTodo(todo.id, todo._fromWeekly)} className="text-slate-300 hover:text-red-500 p-1"><X className="w-4 h-4" /></button>
                            </div>
                          ))
                        )}
                      </div>
                      
                      {!isKWInPast(week.weekNum) ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={selectedMonthWeek === week.weekNum ? newMonthTodoText : ''}
                          onChange={(e) => { setSelectedMonthWeek(week.weekNum); setNewMonthTodoText(e.target.value); }}
                          onFocus={() => setSelectedMonthWeek(week.weekNum)}
                          onKeyDown={(e) => e.key === 'Enter' && addMonthTodo(week.weekNum)}
                          placeholder="+ Neue Aufgabe hinzufügen..."
                          className="flex-1 p-3 border border-slate-200 rounded-lg text-sm bg-white"
                        />
                        <button onClick={() => addMonthTodo(week.weekNum)} className="px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg">
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                      ) : (
                      <div className="text-xs text-slate-400 text-center py-2">Vergangene KW – nicht mehr bearbeitbar</div>
                      )}
                    </div>
                    
                    {/* Button to see all weeks */}
                    <button 
                      onClick={() => setFocusedKW(null)} 
                      className="w-full mt-4 py-2 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
                    >
                      Alle KWs anzeigen →
                    </button>
                  </div>
                );
              })()}

              {/* Full KW Grid (normal view) */}
              {!focusedKW && (
                <div className="flex-1 overflow-y-auto" key={monthTodosRefresh}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {/* "Diesen Monat" tile first */}
                    {(() => {
                      const grouped = getMonthTodosGrouped();
                      const todos = grouped.unassigned || [];
                      const openTodosCount = todos.filter(t => !t.done).length;
                      const doneTodosCount = todos.filter(t => t.done).length;
                      
                      return (
                        <div className="rounded-xl border-2 border-purple-300 bg-purple-50 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-bold text-purple-700">Diesen Monat</span>
                            <div className="flex items-center gap-1 text-xs">
                              {openTodosCount > 0 && <span className="bg-purple-200 text-purple-700 px-1.5 py-0.5 rounded-full">{openTodosCount}</span>}
                              {doneTodosCount > 0 && <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">✓{doneTodosCount}</span>}
                            </div>
                          </div>
                          
                          <div className="space-y-1 mb-2 max-h-[120px] overflow-y-auto">
                            {todos.length === 0 ? (
                              <p className="text-slate-400 text-xs text-center py-2">Keine Aufgaben</p>
                            ) : (
                              todos.map(todo => (
                                <div key={todo.id} className={`p-1.5 rounded-lg flex items-center gap-1.5 text-xs ${todo.done ? 'bg-white/50 text-slate-400' : 'bg-white text-slate-700'}`}>
                                  <button onClick={() => toggleMonthTodo(todo.id, todo._fromWeekly)} className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${todo.done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300'}`}>
                                    {todo.done && <Check className="w-2 h-2 text-white" />}
                                  </button>
                                  <button onClick={() => onEditTodo && onEditTodo(todo, 'monthly', { monthKey: yearMonthKey })} className={`flex-1 truncate text-left text-xs ${todo.done ? 'line-through text-slate-400' : 'text-slate-700 hover:text-purple-700'}`}>{todo.text}</button>
                                  <button onClick={() => deleteMonthTodo(todo.id, todo._fromWeekly)} className="text-slate-300 hover:text-red-500"><X className="w-3 h-3" /></button>
                                </div>
                              ))
                            )}
                          </div>
                          
                          <div className="flex gap-1">
                            <input
                              type="text"
                              value={selectedMonthWeek === 'unassigned' ? newMonthTodoText : ''}
                              onChange={(e) => { setSelectedMonthWeek('unassigned'); setNewMonthTodoText(e.target.value); }}
                              onFocus={() => setSelectedMonthWeek('unassigned')}
                              onKeyDown={(e) => e.key === 'Enter' && addMonthTodo('unassigned')}
                              placeholder="+ Neu..."
                              className="flex-1 p-1.5 border border-slate-200 rounded text-xs bg-white"
                            />
                            <button onClick={() => addMonthTodo('unassigned')} className="p-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded">
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* KW tiles */}
                    {getWeeksInMonth().map(week => {
                      const grouped = getMonthTodosGrouped();
                      const todos = grouped[week.weekNum] || [];
                      const openTodosCount = todos.filter(t => !t.done).length;
                      const doneTodosCount = todos.filter(t => t.done).length;
                      
                      return (
                        <div 
                          key={week.weekNum}
                          id={`month-todo-kw-${week.weekNum}`}
                          className={`rounded-xl border-2 p-3 ${
                            week.isCurrent 
                              ? 'border-emerald-400 bg-emerald-50' 
                              : 'border-slate-200 bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-bold ${week.isCurrent ? 'text-emerald-700' : 'text-slate-700'}`}>
                                KW {week.weekNum}
                              </span>
                              {week.isCurrent && <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">Aktuell</span>}
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              {openTodosCount > 0 && <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">{openTodosCount}</span>}
                              {doneTodosCount > 0 && <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">✓{doneTodosCount}</span>}
                            </div>
                          </div>
                          
                          <p className="text-[10px] text-slate-500 mb-2">{formatDateRange(week)}</p>
                          
                          <div className="space-y-1 mb-2 max-h-[120px] overflow-y-auto">
                            {todos.length === 0 ? (
                              <p className="text-slate-400 text-xs text-center py-2">Keine Aufgaben</p>
                            ) : (
                              todos.map(todo => (
                                <div key={todo.id} className={`p-1.5 rounded-lg flex items-center gap-1.5 text-xs ${todo.done ? 'bg-white/50 text-slate-400' : 'bg-white text-slate-700'}`}>
                                  <button onClick={() => toggleMonthTodo(todo.id, todo._fromWeekly)} className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${todo.done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300'}`}>
                                    {todo.done && <Check className="w-2 h-2 text-white" />}
                                  </button>
                                  <button onClick={() => onEditTodo && onEditTodo(todo, 'monthly', { monthKey: yearMonthKey })} className={`flex-1 truncate text-left text-xs ${todo.done ? 'line-through text-slate-400' : 'text-slate-700 hover:text-purple-700'}`}>{todo.text}</button>
                                  <button onClick={() => deleteMonthTodo(todo.id, todo._fromWeekly)} className="text-slate-300 hover:text-red-500"><X className="w-3 h-3" /></button>
                                </div>
                              ))
                            )}
                          </div>
                          
                          {!isKWInPast(week.weekNum) ? (
                          <div className="flex gap-1">
                            <input
                              type="text"
                              value={selectedMonthWeek === week.weekNum ? newMonthTodoText : ''}
                              onChange={(e) => { setSelectedMonthWeek(week.weekNum); setNewMonthTodoText(e.target.value); }}
                              onFocus={() => setSelectedMonthWeek(week.weekNum)}
                              onKeyDown={(e) => e.key === 'Enter' && addMonthTodo(week.weekNum)}
                              placeholder="+ Neu..."
                              className="flex-1 p-1.5 border border-slate-200 rounded text-xs bg-white"
                            />
                            <button onClick={() => addMonthTodo(week.weekNum)} className="p-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded">
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          ) : (
                          <div className="text-[10px] text-slate-400 text-center py-1">Vergangen</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Kompakter Header: Monatsziel + Monats-Aufgaben (Kalender-Modus) */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
          {/* Monatsziel - einzeilig, nicht editierbar */}
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <Target className="w-4 h-4 text-blue-600 shrink-0" />
            <span className="text-sm font-medium text-slate-700 shrink-0">Monatsziel:</span>
            {wizardData?.monthGoals?.[yearMonthKey] ? (
              <span className={`text-sm truncate flex-1 min-w-0 ${wizardData.monthGoals[yearMonthKey].done ? 'line-through text-slate-400' : 'text-slate-600'}`}>
                {wizardData.monthGoals[yearMonthKey].done && <span className="text-emerald-600 mr-1">✓</span>}
                {wizardData.monthGoals[yearMonthKey].number} {wizardData.monthGoals[yearMonthKey].text}
              </span>
            ) : (
              <span className="text-sm text-slate-400 italic">Nicht geplant</span>
            )}
          </div>
          
          {/* Divider */}
          <div className="hidden sm:block w-px h-6 bg-slate-200 shrink-0"></div>
          
          <div className="flex items-center justify-between w-full sm:w-auto sm:justify-end sm:gap-1">
          {/* Monats-Aufgaben */}
          <div 
            onClick={() => setShowMonthTodosModal(true)}
            className="flex items-center gap-1 sm:gap-2 cursor-pointer hover:bg-purple-50 rounded-lg px-1 py-0.5 sm:px-2 sm:py-1 transition-colors"
          >
            <ListTodo className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <span className="text-sm font-medium text-slate-700 whitespace-nowrap">Aufgaben:</span>
            {(() => {
              const grouped = getMonthTodosGrouped();
              let total = 0, done = 0;
              Object.values(grouped).forEach(todos => {
                total += todos.length;
                done += todos.filter(t => t.done).length;
              });
              return (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">{total - done} offen</span>
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">{done} ✓</span>
                </div>
              );
            })()}
          </div>
          
          {/* Neuer Termin Button - ganz rechts */}
          <button 
            onClick={() => {
              const now = new Date();
              const todayDay = now.getDate();
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(todayDay).padStart(2, '0')}`;
              setSelectedDay(todayDay);
              setEditingAppointment(null);
              setShowColorPicker(false);
              setNewAppointment({ title: '', from: dateStr, to: dateStr, note: '', priority: 5, color: '' });
              setShowAddModal(true);
            }}
            className="w-7 h-7 text-sm font-bold text-white bg-amber-500 hover:bg-amber-600 rounded transition-colors flex items-center justify-center"
            title="Neuer Termin"
            data-testid="month-list-new-appointment-btn"
          >
            <Plus className="w-4 h-4" />
          </button>
          </div>
        </div>
      </div>

      {/* Monatsziel Edit Modal */}
      {editingGoal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-600" />
                Monatsziel - {['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'][month]} {year}
              </h3>
              <button onClick={() => setEditingGoal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea 
              value={goalText} 
              onChange={(e) => setGoalText(e.target.value)} 
              placeholder="Was möchtest du diesen Monat erreichen?" 
              className="w-full p-3 border border-slate-200 rounded-lg text-sm" 
              rows={4} 
              autoFocus 
            />
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSaveGoal} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Speichern</Button>
              <Button onClick={() => setEditingGoal(false)} variant="outline">Abbrechen</Button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Grid - Horizontally Scrollable with Week Column */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-x-auto">
        <div className="min-w-[750px]">
          {/* Day Headers with Week column */}
          <div className="grid grid-cols-[50px_repeat(7,1fr)] border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
            <div className="text-center text-xs font-semibold text-slate-500 py-3 border-r border-slate-200">
              KW
            </div>
            {DAYS_SHORT.map(day => (
              <div key={day} className="text-center text-sm font-semibold text-slate-600 py-3 border-r border-slate-200 last:border-r-0">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Weeks with Week Number column */}
          {weeks.map((weekData, weekIndex) => {
            // Calculate multi-day spans that appear in this week
            const weekSpans = [];
            weekData.days.forEach((day, dayIndex) => {
              if (!day) return;
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              multiDayItems.forEach((item, itemIndex) => {
                const toDate = item.to || item.from;
                if (dateStr >= item.from && dateStr <= toDate) {
                  // Check if already added
                  if (!weekSpans.find(s => s.id === item.id)) {
                    // Calculate span start and end within this week
                    const startDayIndex = weekData.days.findIndex(d => {
                      if (!d) return false;
                      const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                      return ds === item.from;
                    });
                    const endDayIndex = weekData.days.findIndex(d => {
                      if (!d) return false;
                      const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                      return ds === toDate;
                    });
                    
                    // Determine actual visual start/end in this week
                    let visualStart = startDayIndex >= 0 ? startDayIndex : 0;
                    let visualEnd = endDayIndex >= 0 ? endDayIndex : 6;
                    
                    // If item starts before this week, start from first day with value
                    if (item.from < `${year}-${String(month + 1).padStart(2, '0')}-${String(weekData.days[0] || 1).padStart(2, '0')}`) {
                      visualStart = weekData.days.findIndex(d => d !== null);
                    }
                    // If item ends after this week, end at last day with value
                    if (toDate > `${year}-${String(month + 1).padStart(2, '0')}-${String(weekData.days[6] || 28).padStart(2, '0')}`) {
                      visualEnd = weekData.days.map((d, i) => d !== null ? i : -1).filter(i => i >= 0).pop() || 6;
                    }
                    
                    // Calculate total duration for sorting
                    const fromDate = new Date(item.from);
                    const endDate = new Date(toDate);
                    const duration = Math.ceil((endDate - fromDate) / (1000 * 60 * 60 * 24)) + 1;
                    
                    weekSpans.push({
                      ...item,
                      visualStart,
                      visualEnd,
                      isStartInWeek: startDayIndex >= 0,
                      isEndInWeek: endDayIndex >= 0,
                      duration, // For sorting by length
                      multiDayIndex: itemIndex // Index for color assignment
                    });
                  }
                }
              });
            });
            
            // Sort by start date (earliest first) so events are stacked in chronological order
            weekSpans.sort((a, b) => a.from.localeCompare(b.from));
            
            return (
              <div key={weekIndex} className="relative">
                <div className="grid grid-cols-[50px_repeat(7,1fr)] border-b border-slate-200 last:border-b-0">
                  {/* Week Number Column */}
                  <div className="bg-slate-50 border-r border-slate-200 flex flex-col items-center justify-center gap-1 py-1">
                    <span className="text-xs font-bold text-slate-600">
                      {weekData.weekNumber}
                    </span>
                    {/* Transfer to week button (blue) */}
                    <button 
                      className="w-7 h-8 text-base font-bold text-white bg-blue-500 hover:bg-blue-600 rounded transition-colors flex items-center justify-center"
                      onClick={(e) => { e.stopPropagation(); handleOpenTransferModal(weekData.weekNumber, weekData.days); }}
                      title="Übertragen"
                    >
                      →
                    </button>
                    {/* KW Aufgaben button (purple) */}
                    <button 
                      className="w-7 h-8 text-base font-bold text-white bg-purple-500 hover:bg-purple-600 rounded transition-colors flex items-center justify-center"
                      onClick={(e) => { e.stopPropagation(); openMonthTodosForKW(weekData.weekNumber); }}
                      title="KW-Aufgaben"
                    >
                      <ListTodo className="w-4 h-4" />
                    </button>
                    {/* Go to week button (green) */}
                    <button 
                      className="w-7 h-8 text-base font-bold text-white bg-emerald-500 hover:bg-emerald-600 rounded transition-colors flex items-center justify-center"
                      onClick={(e) => { e.stopPropagation(); onGoToWeek && onGoToWeek(weekData.weekNumber, weekData.days); }}
                      title="zur Woche"
                    >
                      ▶
                    </button>
                  </div>
                  
                  {/* Days */}
                  {weekData.days.map((day, dayIndex) => {
                    const isToday = day && today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
                    const singleDayApts = day ? getSingleDayAppointments(day) : [];
                    const isWeekend = dayIndex === 5 || dayIndex === 6;
                    
                    // Get spans that cover this day
                    const spansForThisDay = weekSpans.filter(s => dayIndex >= s.visualStart && dayIndex <= s.visualEnd);
                    
                    // Separate spans into: starts here vs continues through
                    const spansStartingHere = spansForThisDay.filter(s => s.visualStart === dayIndex && s.isStartInWeek);
                    const spansContinuing = spansForThisDay.filter(s => s.visualStart !== dayIndex || !s.isStartInWeek);
                    
                    const hasMany = singleDayApts.length > 3 || spansForThisDay.length > 3;
                    
                    return (
                      <div
                        key={dayIndex}
                        onClick={() => day && handleDayClickInternal(day)}
                        className={`min-h-[120px] sm:min-h-[140px] h-[120px] sm:h-[140px] border-r border-slate-200 last:border-r-0 p-1 cursor-pointer transition-colors flex flex-col
                          ${!day ? 'bg-slate-50' : 'hover:bg-emerald-50'}
                          ${isToday ? 'bg-emerald-100' : ''}
                          ${isWeekend && day ? 'bg-slate-50/50' : ''}
                        `}
                      >
                        {day && (
                          <>
                            {/* Day Number */}
                            <div className={`text-sm font-semibold mb-0.5 flex-shrink-0 ${isToday ? 'text-emerald-700' : 'text-slate-600'}`}>
                              {day}
                            </div>
                            
                            {/* Content area - scrollable */}
                            <div className={`flex-1 space-y-0.5 ${hasMany ? 'overflow-y-auto' : 'overflow-hidden'}`} style={{ scrollbarWidth: 'thin' }}>
                              
                              {/* 1. Multi-day spans CONTINUING through this day - thin colored lines FIRST */}
                              {spansContinuing.map((span) => {
                                // Use consistent color based on multiDayIndex
                                const aptColor = getAppointmentColor(span, span.multiDayIndex || 0);
                                
                                return (
                                  <div 
                                    key={span.id}
                                    onClick={(e) => handleAppointmentClick(span, e)}
                                    className={`h-[4px] rounded-sm cursor-pointer hover:opacity-80 flex-shrink-0 ${aptColor.bg}`}
                                    title={span.title || span.note}
                                  />
                                );
                              })}
                              
                              {/* 2. Multi-day spans STARTING on this day - full bar with text (line-clamp-2) */}
                              {spansStartingHere.map((span) => {
                                const prio = span.priority || 5;
                                const priorityBorderColor = prio >= 8 ? 'border-l-red-500' : 
                                  prio >= 5 ? 'border-l-amber-500' : 'border-l-emerald-500';
                                const displayText = span.title || span.note;
                                const aptColor = getAppointmentColor(span, span.multiDayIndex || 0);
                                const isTransferred = span.transferredToWeek;
                                
                                return (
                                  <div 
                                    key={span.id}
                                    onClick={(e) => handleAppointmentClick(span, e)}
                                    className={`text-[9px] px-1 py-0.5 rounded-r ${aptColor.text} font-medium cursor-pointer hover:opacity-80 line-clamp-2 flex-shrink-0 border-l-4 ${aptColor.bg} ${priorityBorderColor} ${isTransferred ? 'opacity-60' : ''}`}
                                    style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                                    title={displayText}
                                  >
                                    {isTransferred && <span className="mr-0.5">✓</span>}
                                    {displayText}
                                  </div>
                                );
                              })}
                              
                              {/* 3. Single-day appointments */}
                              {singleDayApts.map((apt, idx) => {
                                const prio = apt.priority || 5;
                                const priorityBorderColor = prio >= 8 ? 'border-l-red-500' : 
                                  prio >= 5 ? 'border-l-amber-500' : 'border-l-emerald-500';
                                const aptColor = getAppointmentColor(apt);
                                const isTransferred = apt.transferredToWeek;
                                return (
                                  <div 
                                    key={apt.id || idx}
                                    className={`text-[10px] sm:text-[11px] px-1 py-0.5 rounded-r cursor-pointer hover:opacity-80 line-clamp-2 flex-shrink-0 border-l-4 ${aptColor.bg} ${aptColor.text} ${priorityBorderColor} ${isTransferred ? 'opacity-60' : ''} ${apt.done ? 'opacity-70' : ''}`}
                                    style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                                    title={apt.title || apt.name}
                                    onClick={(e) => handleAppointmentClick(apt, e)}
                                  >
                                    {isTransferred && <span className="mr-0.5">✓</span>}
                                    {apt.done && !isTransferred && <span className="mr-0.5">☑</span>}
                                    {apt.isHabitReminder && apt.completed && <span className="mr-0.5 text-emerald-600">✓</span>}
                                    <span className={`${apt.isHabitReminder && apt.completed ? 'line-through opacity-70' : ''} ${apt.done && !apt.isHabitReminder ? 'line-through' : ''}`}>{apt.title || apt.name}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Monats-Aufgaben Modal */}
      {showMonthTodosModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-xl p-4 shadow-xl max-h-[85vh] overflow-hidden flex flex-col ${focusedKW ? 'max-w-md w-full' : 'max-w-4xl w-full'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <ListTodo className="w-5 h-5 text-purple-600" />
                {focusedKW 
                  ? `KW ${focusedKW} Aufgaben`
                  : `Monats-Aufgaben - ${['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'][month]} ${year}`
                }
              </h3>
              <button onClick={() => { setShowMonthTodosModal(false); setFocusedKW(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Single KW View (when opened from calendar) */}
            {focusedKW && (() => {
              const week = getWeeksInMonth().find(w => w.weekNum === focusedKW);
              if (!week) return null;
              
              const grouped = getMonthTodosGrouped();
              const todos = grouped[week.weekNum] || [];
              const openTodos = todos.filter(t => !t.done).length;
              const doneTodos = todos.filter(t => t.done).length;
              
              return (
                <div className="flex-1 overflow-y-auto" key={monthTodosRefresh}>
                  <div className={`rounded-xl border-2 p-4 ${week.isCurrent ? 'border-emerald-400 bg-emerald-50' : 'border-purple-400 bg-purple-50'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-lg font-bold ${week.isCurrent ? 'text-emerald-700' : 'text-purple-700'}`}>
                          KW {week.weekNum}
                        </span>
                        {week.isCurrent && <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full">Aktuell</span>}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        {openTodos > 0 && <span className="bg-purple-200 text-purple-700 px-2 py-1 rounded-full">{openTodos} offen</span>}
                        {doneTodos > 0 && <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">✓ {doneTodos}</span>}
                      </div>
                    </div>
                    
                    <p className="text-sm text-slate-500 mb-4">{formatDateRange(week)}</p>
                    
                    <div className="space-y-2 mb-4 max-h-[300px] overflow-y-auto">
                      {todos.length === 0 ? (
                        <p className="text-slate-400 text-sm text-center py-4">Keine Aufgaben für diese Woche</p>
                      ) : (
                        todos.map(todo => (
                          <div key={todo.id} className={`p-3 rounded-lg flex items-center gap-3 ${todo.done ? 'bg-white/50 text-slate-400' : 'bg-white text-slate-700'}`}>
                            <button onClick={() => toggleMonthTodo(todo.id, todo._fromWeekly)} className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${todo.done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 hover:border-emerald-400'}`}>
                              {todo.done && <Check className="w-3 h-3 text-white" />}
                            </button>
                            <button onClick={() => onEditTodo && onEditTodo(todo, 'monthly', { monthKey: yearMonthKey })} className={`flex-1 text-left text-sm ${todo.done ? 'line-through text-slate-400' : 'text-slate-700 hover:text-purple-700'}`}>{todo.text}</button>
                            <button onClick={() => deleteMonthTodo(todo.id, todo._fromWeekly)} className="text-slate-300 hover:text-red-500 p-1"><X className="w-4 h-4" /></button>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {!isKWInPast(week.weekNum) ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={selectedMonthWeek === week.weekNum ? newMonthTodoText : ''}
                        onChange={(e) => { setSelectedMonthWeek(week.weekNum); setNewMonthTodoText(e.target.value); }}
                        onFocus={() => setSelectedMonthWeek(week.weekNum)}
                        onKeyDown={(e) => e.key === 'Enter' && addMonthTodo(week.weekNum)}
                        placeholder="+ Neue Aufgabe hinzufügen..."
                        className="flex-1 p-3 border border-slate-200 rounded-lg text-sm bg-white"
                      />
                      <button onClick={() => addMonthTodo(week.weekNum)} className="px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg">
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                    ) : (
                    <div className="text-xs text-slate-400 text-center py-2">Vergangene KW – nicht mehr bearbeitbar</div>
                    )}
                  </div>
                  
                  {/* Button to see all weeks */}
                  <button 
                    onClick={() => setFocusedKW(null)} 
                    className="w-full mt-4 py-2 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    Alle KWs anzeigen →
                  </button>
                </div>
              );
            })()}

            {/* Full KW Grid (normal view) */}
            {!focusedKW && (
              <div className="flex-1 overflow-y-auto" key={monthTodosRefresh}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* "Diesen Monat" tile first */}
                  {(() => {
                    const grouped = getMonthTodosGrouped();
                    const todos = grouped.unassigned || [];
                    const openTodos = todos.filter(t => !t.done).length;
                    const doneTodos = todos.filter(t => t.done).length;
                    
                    return (
                      <div className="rounded-xl border-2 border-purple-300 bg-purple-50 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-bold text-purple-700">Diesen Monat</span>
                          <div className="flex items-center gap-1 text-xs">
                            {openTodos > 0 && <span className="bg-purple-200 text-purple-700 px-1.5 py-0.5 rounded-full">{openTodos}</span>}
                            {doneTodos > 0 && <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">✓{doneTodos}</span>}
                          </div>
                        </div>
                        
                        <div className="space-y-1 mb-2 max-h-[120px] overflow-y-auto">
                          {todos.length === 0 ? (
                            <p className="text-slate-400 text-xs text-center py-2">Keine Aufgaben</p>
                          ) : (
                            todos.map(todo => (
                              <div key={todo.id} className={`p-1.5 rounded-lg flex items-center gap-1.5 text-xs ${todo.done ? 'bg-white/50 text-slate-400' : 'bg-white text-slate-700'}`}>
                                <button onClick={() => toggleMonthTodo(todo.id, todo._fromWeekly)} className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${todo.done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300'}`}>
                                  {todo.done && <Check className="w-2 h-2 text-white" />}
                                </button>
                                <button onClick={() => onEditTodo && onEditTodo(todo, 'monthly', { monthKey: yearMonthKey })} className={`flex-1 truncate text-left text-xs ${todo.done ? 'line-through text-slate-400' : 'text-slate-700 hover:text-purple-700'}`}>{todo.text}</button>
                                <button onClick={() => deleteMonthTodo(todo.id, todo._fromWeekly)} className="text-slate-300 hover:text-red-500"><X className="w-3 h-3" /></button>
                              </div>
                            ))
                          )}
                        </div>
                        
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={selectedMonthWeek === 'unassigned' ? newMonthTodoText : ''}
                            onChange={(e) => { setSelectedMonthWeek('unassigned'); setNewMonthTodoText(e.target.value); }}
                            onFocus={() => setSelectedMonthWeek('unassigned')}
                            onKeyDown={(e) => e.key === 'Enter' && addMonthTodo('unassigned')}
                            placeholder="+ Neu..."
                            className="flex-1 p-1.5 border border-slate-200 rounded text-xs bg-white"
                          />
                          <button onClick={() => addMonthTodo('unassigned')} className="p-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded">
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* KW tiles */}
                  {getWeeksInMonth().map(week => {
                    const grouped = getMonthTodosGrouped();
                    const todos = grouped[week.weekNum] || [];
                    const openTodos = todos.filter(t => !t.done).length;
                    const doneTodos = todos.filter(t => t.done).length;
                    
                    return (
                      <div 
                        key={week.weekNum}
                        id={`month-todo-kw-${week.weekNum}`}
                        className={`rounded-xl border-2 p-3 ${
                          week.isCurrent 
                            ? 'border-emerald-400 bg-emerald-50' 
                            : 'border-slate-200 bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold ${week.isCurrent ? 'text-emerald-700' : 'text-slate-700'}`}>
                              KW {week.weekNum}
                            </span>
                            {week.isCurrent && <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">Aktuell</span>}
                          </div>
                          <div className="flex items-center gap-1 text-xs">
                            {openTodos > 0 && <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">{openTodos}</span>}
                            {doneTodos > 0 && <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">✓{doneTodos}</span>}
                          </div>
                        </div>
                        
                        <p className="text-[10px] text-slate-500 mb-2">{formatDateRange(week)}</p>
                        
                        <div className="space-y-1 mb-2 max-h-[120px] overflow-y-auto">
                          {todos.length === 0 ? (
                            <p className="text-slate-400 text-xs text-center py-2">Keine Aufgaben</p>
                          ) : (
                            todos.map(todo => (
                              <div key={todo.id} className={`p-1.5 rounded-lg flex items-center gap-1.5 text-xs ${todo.done ? 'bg-white/50 text-slate-400' : 'bg-white text-slate-700'}`}>
                                <button onClick={() => toggleMonthTodo(todo.id, todo._fromWeekly)} className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${todo.done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300'}`}>
                                  {todo.done && <Check className="w-2 h-2 text-white" />}
                                </button>
                                <button onClick={() => onEditTodo && onEditTodo(todo, 'monthly', { monthKey: yearMonthKey })} className={`flex-1 truncate text-left text-xs ${todo.done ? 'line-through text-slate-400' : 'text-slate-700 hover:text-purple-700'}`}>{todo.text}</button>
                                <button onClick={() => deleteMonthTodo(todo.id, todo._fromWeekly)} className="text-slate-300 hover:text-red-500"><X className="w-3 h-3" /></button>
                              </div>
                            ))
                          )}
                        </div>
                        
                        {!isKWInPast(week.weekNum) ? (
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={selectedMonthWeek === week.weekNum ? newMonthTodoText : ''}
                            onChange={(e) => { setSelectedMonthWeek(week.weekNum); setNewMonthTodoText(e.target.value); }}
                            onFocus={() => setSelectedMonthWeek(week.weekNum)}
                            onKeyDown={(e) => e.key === 'Enter' && addMonthTodo(week.weekNum)}
                            placeholder="+ Neu..."
                            className="flex-1 p-1.5 border border-slate-200 rounded text-xs bg-white"
                          />
                          <button onClick={() => addMonthTodo(week.weekNum)} className="p-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded">
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        ) : (
                        <div className="text-[10px] text-slate-400 text-center py-1">Vergangen</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-between items-center pt-4 border-t border-slate-200 mt-4">
              <div className="text-sm text-slate-500">
                {(() => {
                  const grouped = getMonthTodosGrouped();
                  let total = 0, done = 0;
                  Object.values(grouped).forEach(todos => {
                    total += todos.length;
                    done += todos.filter(t => t.done).length;
                  });
                  return `${done}/${total} Aufgaben erledigt`;
                })()}
              </div>
              <Button onClick={() => setShowMonthTodosModal(false)} variant="outline">
                Schließen
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Appointment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">
                {editingAppointment ? 'Termin bearbeiten' : 'Neuer Termin'}
              </h3>
              <button onClick={() => { setShowAddModal(false); setEditingAppointment(null); setShowColorPicker(false); setShowDeleteConfirm(false); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {showDeleteConfirm ? (
              <div className="text-center py-4">
                <p className="text-slate-700 mb-2">Termin wirklich löschen?</p>
                <p className="text-sm text-slate-500 mb-4">Der Termin wird aus allen Ansichten (Jahr, Monat, Woche) entfernt.</p>
                <div className="flex gap-3 justify-center">
                  <Button onClick={executeDelete} className="bg-red-600 hover:bg-red-700 text-white">
                    <Trash2 className="w-4 h-4 mr-2" /> Löschen
                  </Button>
                  <Button onClick={() => setShowDeleteConfirm(false)} variant="outline">
                    Abbrechen
                  </Button>
                </div>
              </div>
            ) : (
            <div className="space-y-4">
              {/* Title with color picker */}
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <label className="text-sm font-medium text-slate-700">Titel</label>
                  <input
                    type="text"
                    value={newAppointment.title}
                    onChange={(e) => setNewAppointment({ ...newAppointment, title: e.target.value })}
                    placeholder="Termin Titel"
                    className="w-full mt-1 p-2 border border-slate-200 rounded-lg text-sm"
                    autoFocus
                  />
                </div>
                {/* Color picker button */}
                <div className="relative pt-5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowColorPicker(!showColorPicker);
                    }}
                    className={`w-10 h-10 rounded-full ${currentColorData.bg} shadow-md active:scale-95 transition-all ring-2 ring-white`}
                    style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                    title="Farbe wählen"
                  />
                  {showColorPicker && (
                    <div 
                      className="absolute bg-white rounded-xl shadow-xl border border-slate-200 p-3 z-[9999]"
                      style={{ right: '-10px', top: '52px', minWidth: '160px' }}
                    >
                      <p className="text-xs text-slate-500 mb-2 text-center font-medium">Farbe wählen</p>
                      <div className="grid grid-cols-4 gap-3">
                        {COLOR_KEYS.map((colorKey) => {
                          const colorData = APPOINTMENT_COLORS[colorKey];
                          const isSelected = currentColorKey === colorKey;
                          return (
                            <button
                              key={colorKey}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setNewAppointment({ ...newAppointment, color: colorKey });
                                setShowColorPicker(false);
                              }}
                              className={`w-8 h-8 rounded-full transition-all ${colorData.bg} ${
                                isSelected ? 'ring-2 ring-offset-2 ring-slate-800 scale-110' : 'active:scale-90'
                              }`}
                              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Von</label>
                  <input
                    type="date"
                    value={newAppointment.from}
                    onChange={(e) => {
                      const newFrom = e.target.value;
                      const newTo = newAppointment.to && newAppointment.to < newFrom ? newFrom : newAppointment.to;
                      setNewAppointment({ ...newAppointment, from: newFrom, to: newTo || newFrom });
                    }}
                    className="w-full mt-1 p-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Bis</label>
                  <input
                    type="date"
                    value={newAppointment.to}
                    min={newAppointment.from}
                    onChange={(e) => setNewAppointment({ ...newAppointment, to: e.target.value })}
                    className="w-full mt-1 p-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Priorität: {newAppointment.priority}</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={newAppointment.priority}
                  onChange={(e) => setNewAppointment({ ...newAppointment, priority: parseInt(e.target.value) })}
                  className="w-full mt-1 accent-emerald-600"
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>1 (niedrig)</span>
                  <span>10 (hoch)</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700">Notiz (optional)</label>
                <textarea
                  value={newAppointment.note}
                  onChange={(e) => setNewAppointment({ ...newAppointment, note: e.target.value })}
                  placeholder="Zusätzliche Infos..."
                  className="w-full mt-1 p-2 border border-slate-200 rounded-lg text-sm resize-none"
                  rows={2}
                />
              </div>

              {/* Kontakt-Verknüpfung (Calendar Grid Modal) */}
              <div ref={contactSearchRef} className="relative">
                <label className="text-sm font-medium text-slate-700">Kontakt (optional)</label>
                {newAppointment.contactRef ? (
                  <div className="flex items-center gap-2 mt-1 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <User className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm text-emerald-700 font-medium flex-1">{newAppointment.contactRef.name}</span>
                    <button type="button" onClick={() => { setNewAppointment({...newAppointment, contactRef: null}); setContactSearch(''); }} className="text-emerald-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <>
                    <div className="relative mt-1">
                      <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                      <input type="text" value={contactSearch} onChange={e => { setContactSearch(e.target.value); setContactSearchOpen(true); }}
                        onFocus={() => setContactSearchOpen(true)} placeholder="Kontakt suchen..."
                        className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                    </div>
                    {contactSearchOpen && contactResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 z-50 bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                        {contactResults.map(c => (
                          <button key={c._id || c.id} type="button" onMouseDown={e => e.preventDefault()}
                            onClick={() => { setNewAppointment({...newAppointment, contactRef: { id: c._id || c.id, typ: 'teampartner', name: c.name }}); setContactSearch(''); setContactSearchOpen(false); }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-emerald-50 flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-slate-400" />{c.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSaveAppointment} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {editingAppointment ? 'Speichern' : 'Hinzufügen'}
                </Button>
                {editingAppointment && (
                  <Button onClick={handleDeleteAppointment} variant="outline" className="text-red-600 border-red-300 hover:bg-red-50">
                    Löschen
                  </Button>
                )}
                <Button onClick={() => { setShowAddModal(false); setEditingAppointment(null); setShowDeleteConfirm(false); setContactSearch(''); }} variant="outline">
                  Abbrechen
                </Button>
              </div>
              {editingAppointment?.id && editingAppointment.contactRef && (
                <button
                  type="button"
                  onClick={() => handleToggleMonthDone(editingAppointment)}
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all mt-1 ${
                    editingAppointment.done
                      ? 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                  }`}
                >
                  <Check className="w-4 h-4" />
                  {editingAppointment.done ? 'Als offen markieren' : 'Erledigt'}
                </button>
              )}
            </div>
            )}
          </div>
        </div>
      )}

      {/* Step-by-Step Transfer Modal */}
      {showTransferModal && transferWeekData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-blue-600 text-white px-4 py-3 flex-shrink-0">
              <h3 className="text-lg font-semibold">
                Übertragen zur KW {transferWeekData.weekNumber}
              </h3>
              <p className="text-sm text-blue-100">
                {currentTransferIndex + 1} von {transferItems.length} Einträgen
              </p>
            </div>

            {/* Progress bar */}
            <div className="h-1 bg-blue-100 flex-shrink-0">
              <div 
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${((currentTransferIndex + 1) / transferItems.length) * 100}%` }}
              />
            </div>

            {/* Content - scrollable */}
            <div className="flex-1 overflow-y-auto p-4">
              {getCurrentTransferItem() && (() => {
                const item = getCurrentTransferItem();
                const isMultiDay = item.from !== item.to;
                const currentColor = transferEditData.color ?? item.color ?? (isMultiDay ? DEFAULT_MULTIDAY_COLOR : DEFAULT_SINGLEDAY_COLOR);
                const colorData = APPOINTMENT_COLORS[currentColor] || APPOINTMENT_COLORS[DEFAULT_SINGLEDAY_COLOR];

                return (
                  <div className="space-y-4">
                    {/* Title with Color Dot */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Titel</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={transferEditData.title ?? item.title ?? item.note ?? ''}
                          onChange={(e) => updateTransferEditData('title', e.target.value)}
                          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Titel eingeben"
                        />
                        {/* Color dot with dropdown */}
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowTransferColorPicker(!showTransferColorPicker)}
                            className={`w-8 h-8 rounded-full ${colorData.bg} border-2 border-white shadow-md hover:scale-110 transition-transform flex-shrink-0`}
                            title="Farbe wählen"
                          />
                          {showTransferColorPicker && (
                            <div className="absolute top-full right-0 mt-2 p-2 bg-white rounded-lg shadow-xl border border-slate-200 z-50 flex flex-wrap gap-1.5 w-[140px]">
                              {COLOR_KEYS.map((colorKey) => {
                                const cd = APPOINTMENT_COLORS[colorKey];
                                const isSelected = currentColor === colorKey;
                                return (
                                  <button
                                    key={colorKey}
                                    type="button"
                                    onClick={() => {
                                      updateTransferEditData('color', colorKey);
                                      setShowTransferColorPicker(false);
                                    }}
                                    className={`w-7 h-7 rounded-full ${cd.bg} transition-all ${isSelected ? 'ring-2 ring-offset-1 ring-slate-700 scale-110' : 'hover:scale-105'}`}
                                    title={colorKey}
                                  />
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Date range */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Von</label>
                        <input
                          type="date"
                          value={transferEditData.from ?? item.from ?? ''}
                          onChange={(e) => updateTransferEditData('from', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Bis</label>
                        <input
                          type="date"
                          value={transferEditData.to ?? item.to ?? item.from ?? ''}
                          onChange={(e) => updateTransferEditData('to', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Time range */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Startzeit</label>
                        <input
                          type="time"
                          value={transferEditData.startTime ?? item.startTime ?? '09:00'}
                          onChange={(e) => handleTransferStartTimeChange(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Endzeit</label>
                        <input
                          type="time"
                          value={transferEditData.endTime ?? item.endTime ?? '10:00'}
                          onChange={(e) => updateTransferEditData('endTime', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Priority */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Priorität: {transferEditData.priority ?? item.priority ?? 5}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={transferEditData.priority ?? item.priority ?? 5}
                        onChange={(e) => updateTransferEditData('priority', parseInt(e.target.value))}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>Niedrig</span>
                        <span>Hoch</span>
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Notizen</label>
                      <textarea
                        value={transferEditData.note ?? (item.note && !item.note.match(/^\d{2}:\d{2}-\d{2}:\d{2}$/) ? item.note : '')}
                        onChange={(e) => updateTransferEditData('note', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        placeholder="Notizen hinzufügen..."
                      />
                    </div>

                    {/* Source indicator */}
                    <div className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                      Quelle: {item.source === 'month' ? 'Monatsansicht' : 'Jahresansicht'}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Actions */}
            <div className="border-t border-slate-200 p-4 flex-shrink-0 bg-slate-50">
              {/* Overlap Error */}
              {transferOverlapError && (
                <div className="mb-3 flex items-center gap-2 text-red-600 bg-red-50 p-2 rounded-lg">
                  <span className="text-lg">⚠️</span>
                  <p className="text-sm">{transferOverlapError}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 mb-2">
                <button
                  onClick={handleTransferNext}
                  className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                >
                  ✓ Speichern
                </button>
                <button
                  onClick={handleTransferSkip}
                  className="px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium rounded-lg transition-colors"
                >
                  ⟫ Überspringen
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleTransferToTodo}
                  className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors"
                >
                  ☐ To-dos
                </button>
                <button
                  onClick={handleTransferCancel}
                  className="px-4 py-2.5 bg-red-100 hover:bg-red-200 text-red-700 font-medium rounded-lg transition-colors"
                >
                  ✕ Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
